import { Component } from '@theme/component';
import { CartAddEvent, CartUpdateEvent, ThemeEvents } from '@theme/events';
import { CART_ITEM_TYPES, CART_PROPERTY_KEYS } from '@theme/cart-contract';
import { fetchCartJson } from '@theme/cart-state';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { hideZendeskLauncher, restoreZendeskLauncher } from '@theme/zendesk-launcher';
import { formatMoney as formatShopifyMoney } from '@theme/money-formatting';
import { decodeTranslatedText } from '@theme/translated-copy';
import { AVAILABILITY_API } from '@theme/ea-api-endpoints';
import {
  fetchArrangementAvailability,
  resolveWindow,
  isArrangementCardAvailable,
  readArrangementCardFlags,
  orderTypeFor,
} from '@theme/ea-arrangements-availability';
import { readStoreSelection } from '@theme/store-availability';
import {
  fetchProductAvailability,
  eligibleStoreIdsFor,
  buildStoreState,
  saveStoreContext,
  syncCartAttributes,
  updateStoreTriggers,
  triggerDeliveryLabel,
  emitStoreChange,
  canonicalEaId,
  eaIdDigits,
} from '@theme/ea-availability';

const ZENDESK_HIDE_SOURCE = 'bundle-builder';

/** localStorage key for the in-progress bundle draft (selected items + quantities). */
const BB_STORAGE_KEY = 'ea:bb-drawer:draft';
/** Drafts older than this are discarded on restore (stale prices / removed products). */
const BB_DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
/**
 * Draft schema version. Bumped to 2 for EA-3873: v1 drafts couldn't distinguish committed
 * from uncommitted items on restore (every restored item was forced to draft:true and
 * re-saved), so a cleared bundle cart left a stale draft that looked like a genuine
 * in-progress one and lingered across reloads. A restore now DISCARDS any draft whose
 * version isn't current, clearing those corrupted v1 drafts once; v2 preserves the
 * committed/uncommitted flag so #syncFromCart can drop cart-unbacked committed items.
 */
const BB_DRAFT_VERSION = 2;

/** localStorage key the store selector writes the chosen store/delivery selection to. */
const STORE_STORAGE_KEY = 'ea-store-availability';

/** How many background v4 prefetch calls may run at once after a store is selected (EA-3873 PR-2). */
const PREFETCH_CONCURRENCY = 3;

/**
 * @typedef {Object} BundleItem
 * @property {number} variantId
 * @property {number} productId
 * @property {string} title
 * @property {string} image
 * @property {number} price
 * @property {number} compareAtPrice
 * @property {string} handle
 * @property {string} badge
 * @property {string} variantTitle
 * @property {number} quantity
 * @property {string} [externalVariantId] - country-prefixed marketplace variant id (e.g. "US-18517"), for the pre-checkout availability check
 * @property {boolean} [isAddon] - true if item was added from Step 2 (add-on)
 */

/**
 * Bundle Builder Drawer component.
 * Renders a right-aligned slide-in panel for building product bundles.
 * Adding any item opens a secondary drawer (variant picker) so the per-variant
 * fulfillment availability API runs before the item joins the bundle — this
 * holds for single-/default-variant products too. After confirming, the product
 * card shows a quantity stepper.
 * @extends {Component}
 */
class EaBundleBuilderDrawer extends Component {
  /** @type {BundleItem[]} */
  #selectedItems = [];

  /**
   * Monotonic counter stamped onto each selected item as `addOrder` so cards can be
   * sorted "last added first" (EA-3873). Increasing forever, so removing an item from
   * the middle never disturbs the relative order of the rest. Restored drafts absorb
   * their persisted max so new adds keep climbing above them.
   * @type {number}
   */
  #addOrderCounter = 0;

  /** @type {Set<number>} Variant ids already committed as bundle lines in the cart (skipped on Add All). */
  #committedVariantIds = new Set();

  /** @type {boolean} */
  #isOpen = false;

  /** @type {number|null} Pending close-hide timer, cancelled on reopen. */
  #closeTimer = null;

  /** @type {string} */
  #badgeIconUrl = '';

  /** @type {number} Max quantity per bundle item (theme setting) */
  #maxQuantity = 9;

  /** @type {number} Minimum distinct products required to enable Add to Cart. */
  #minBundleItems = 2;

  /** @type {object|null} Context for the open secondary drawer */
  #secondaryCtx = null;

  /** True while the variant-availability API is in flight (CTA stays disabled). */
  #secondaryLoading = false;

  /** Availability-context key the open picker was last resolved against (null: no store). */
  #secondaryResolvedKey = null;

  /**
   * Per-variant fulfillment capabilities, keyed by availability context
   * (`${storeId}:${date}:${fulfillmentType}`) → Map(productId → capabilities).
   * Filled on demand when the secondary drawer opens; cached per context so
   * reopening the same product's picker (same store) doesn't refetch.
   * @type {Map<string, Map<number, object>>}
   */
  #capsByContext = new Map();

  /**
   * EA-4071: the stores v4 names as able to SHIP each external variant id, from
   * `shipment[].stores[]` on entries marked available. A shipment-only zip is saved with no
   * storeId, and the add-to-cart interceptor refuses any add whose selection names no store
   * (it reopens the store selector instead) — so Add All to Cart looped on that selector.
   * #adoptShipmentStoreForBundle reads this to write the shipping store into the selection
   * first, the way the personalization drawer's #adoptShipmentStore does.
   * @type {Map<number, string[]>}
   */
  #shipmentStoresByExtId = new Map();

  /**
   * In-flight v4 calls, keyed `${ctxKey}:${productId}`, so a background prefetch and an
   * on-Add click for the same card SHARE one request instead of firing two (EA-3873 PR-2).
   * Deleted when the call settles, so a failed one can be retried.
   * @type {Map<string, Promise<Map<number, object>>>}
   */
  #cardFetchPromises = new Map();

  /**
   * Background prefetch queue of PRIMARY cards still to warm, and its generation guard.
   * A store/date/fulfilment change bumps the generation, empties the queue, and lets the
   * pump loop bail — so a superseded selection can't keep warming the wrong context.
   * @type {{ productId: number, extIds: number[] }[]}
   */
  #prefetchQueue = [];
  #prefetchGen = 0;
  #prefetchActive = 0;
  /**
   * The context the current queue is warming for. Retained so a job settling from an OLDER
   * generation can resume the CURRENT queue (with this context) instead of bailing — otherwise,
   * if a store change bumped the generation while all concurrency slots were held by in-flight
   * old-gen jobs, the new queue would stall. Cleared when #startPrefetch has no usable context.
   * @type {object|null}
   */
  #prefetchCtx = null;

  /**
   * True while priority-1 availability (arrangements + v4 for the bundle/cart) is in flight
   * after a store change/load. Blocks the footer ATC and shimmers the carousels until it
   * settles, so a shopper can't check out on a bundle that may still lose an item (EA-3873).
   * @type {boolean}
   */
  #priorityLoading = false;

  /** @type {ReturnType<typeof setTimeout>|null} Removes the one-shot first-accordion attention shimmer. */
  #attentionTimer = null;

  /** @type {WeakMap<HTMLElement, string>} Original card pricing HTML per pricing element */
  #originalPricing = new WeakMap();

  /** @type {{ freeDelivery: number, tiers: {threshold:number, type:'percentage'|'fixed', value:number, upTo:number|null}[] }} */
  #discountConfig = { freeDelivery: 0, tiers: [] };

  /** @type {{ threshold: number, label: string }[]} Progress markers, ascending */
  #markers = [];

  /** @type {number} Largest threshold among markers (bar scale) */
  #maxThreshold = 0;

  /** @type {HTMLElement|null} Element that triggered the drawer open (restored on close) */
  #triggerElement = null;

  /** @type {Set<number>} Product ids the availability API reported as not fulfillable
   * for the current store/delivery — their Add CTA is disabled and they can't be added
   * (cleared when the store selection changes). */
  #unavailableProducts = new Set();

  /** Monotonic guard so a slower store-change reconciliation can't overwrite the
   * result of a newer one (rapid store switches). */
  #storeReconcileGen = 0;

  /** Monotonic guard so a slower #syncFromCart response can't overwrite a newer one
   * (concurrent calls from #hydrate, open() and cart:update). */
  #cartSyncGen = 0;

  /**
   * Has the drawer's markup been realised yet? The section ships it inside a
   * <template> (see sections/ea-bundle-builder-drawer.liquid): parsed, but not built
   * into elements, until the shopper asks for the drawer.
   */
  #hydrated = false;

  /**
   * A `store:availability:updated` that arrived while the drawer was still a
   * <template>. Its handler is skipped then — there is nothing to re-filter and no
   * footer to re-gate — so the work is replayed once at #hydrate, and the shopper who
   * changes store and THEN opens the drawer sees exactly what they saw before.
   */
  #missedStoreChange = false;

  /**
   * Build the drawer's contents and run the setup that needs them.
   *
   * This is the second half of what `connectedCallback` used to do on every page of
   * the store. The order is unchanged — accordions, tabs, card buttons, progress
   * markers, the saved draft, progress, footer, then the cart sync — so the drawer
   * reaches exactly the state it reached before; it reaches it on first open instead
   * of on every page load.
   *
   * Idempotent: the template is removed once cloned, and the flag guards the rest.
   */
  #hydrate() {
    if (this.#hydrated) return;
    this.#hydrated = true;

    const template = this.querySelector('template[data-bb-content]');
    if (template) {
      const host = template.parentElement;
      host?.insertBefore(template.content.cloneNode(true), template);
      template.remove();
    }

    this.#initAccordions();
    this.#initTabs();
    this.#bindCardButtons();
    this.#renderProgressMarkers();
    this.#restoreState();
    this.#updateProgress();
    this.#updateFooter();
    // The footer store/delivery pills live inside the <template> above, so they
    // enter the DOM only now — after store-availability.js already filled every
    // live trigger during its page-load restore. Reflect the persisted selection
    // into the freshly-cloned footer so a pre-selected store is shown on open.
    this.#reflectStoreSelection();
    this.#syncFromCart();

    // Replay a store change that landed while there was nothing to apply it to.
    if (this.#missedStoreChange) {
      this.#missedStoreChange = false;
      this.#clearUnavailableProducts();
    }
    // Warm availability for the already-selected store: priority-1 (bundle/cart) reconcile
    // first, then background-prefetch the rest of the primary cards so their pickers open
    // instantly (EA-3873 PR-2). Covers both a fresh load and the missed-store-change replay.
    this.#warmAvailability();
  }

  connectedCallback() {
    super.connectedCallback();
    this.#badgeIconUrl = this.dataset.badgeIconUrl || '';
    this.#maxQuantity = Math.max(1, Number(this.dataset.maxQuantity) || 9);
    this.#parseDiscountConfig();
    this.#bindTriggers();
    // Everything that reads or writes the drawer's own markup now waits for
    // #hydrate(). What remains here is what has to work before the drawer exists:
    // the document-level trigger handler, the event listeners below, and the global
    // open/close functions other components call.

    // Close on Escape key
    document.addEventListener('keydown', this.#handleKeyDown);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    // Availability is store-specific — re-enable any products marked unavailable
    // when the store selection changes so they're re-checked on the next Add.
    document.addEventListener('store:availability:updated', this.#handleStoreAvailabilityChange);
    // A selection landing while the embedded selector is up — from the panel itself, or
    // from the header with the drawer open.
    document.addEventListener('store-availability:change', this.#handleLocatorSelectionChange);

    // Clicking the footer's zip / delivery-date fields opens the EMBEDDED locator (not the
    // header pop-up). Capture phase so this pre-empts store-availability.js's bubble-phase
    // document handler for the same [data-store-availability-trigger].
    this.addEventListener('click', this.#onFooterInputClick, true);

    // Per-variant fulfillment availability (second API) is fetched ON DEMAND when
    // the secondary (variant-picker) drawer opens — see #applyVariantAvailability.
    // No background prefetch.

    // Expose global open/close functions for external triggers
    window.openBundleBuilderDrawer = () => this.open();
    window.closeBundleBuilderDrawer = () => this.close();
    // Page product cards (bundle-builder page) call this to start the Add flow.
    window.bundleBuilderAddProduct = (productId) => this.addProductById(productId);
    // Warm availability WITHOUT opening the drawer. The BB page calls this on load so the
    // drawer hydrates (its cards enter the DOM) and its store-change listener starts firing
    // v4 the moment a store is selected — otherwise the prefetch only began on first open
    // (the drawer is unhydrated until then, so #handleStoreAvailabilityChange no-ops). Hydrate
    // warms once; a call after it's already hydrated re-warms for the current selection.
    window.bundleBuilderWarm = () => {
      const wasHydrated = this.#hydrated;
      this.#hydrate();
      if (wasHydrated) this.#warmAvailability();
    };

    // Bound by attribute, not by `ref`: the close sits inside
    // `<store-availability-component>`, and a `ref` there belongs to THAT component.
    this.#locatorClose()?.addEventListener('click', () => this.#hideLocator());
  }

  /** Parse the market discount config supplied by Liquid */
  #parseDiscountConfig() {
    try {
      const parsed = JSON.parse(this.dataset.discountConfig || '{}');
      this.#discountConfig = {
        freeDelivery: Number(parsed.freeDelivery) || 0,
        tiers: Array.isArray(parsed.tiers)
          ? parsed.tiers.map((t) => {
              // Back-compat: legacy tiers have no `type` and store the percent in `percentage`.
              const type = t.type === 'fixed' ? 'fixed' : 'percentage';
              const value = Number(t.value != null ? t.value : t.percentage) || 0;
              const upTo = t.upTo != null ? Number(t.upTo) : null;
              return {
                threshold: Number(t.threshold),
                type,
                value,
                upTo: Number.isFinite(upTo) && upTo > 0 ? upTo : null,
              };
            })
          : [],
      };
    } catch {
      this.#discountConfig = { freeDelivery: 0, tiers: [] };
    }
  }

  /**
   * Free delivery only applies to next-day-and-beyond windows — the same rule the
   * rest of the catalog follows. Returns false when the shopper has a Same Day or
   * 1-Hour window selected in the store selector, so the "free delivery unlocked"
   * prefix is not shown on the bundle discount progress bar (its threshold still
   * governs free delivery on a Next Day / later order via the normal delivery rules). Also
   * false on shipment: shipping is always charged at checkout, whatever the window
   * label says (EA-3968); and false on pickup, which never incurs a delivery charge
   * so the free-delivery prefix is irrelevant.
   * @returns {boolean}
   */
  #freeDeliveryApplies() {
    const sel = readStoreSelection();
    const ft = sel?.fulfillmentType;
    if (ft === 'shipment' || ft === 'pickup') return false;
    const type = sel?.deliveryType;
    return type !== 'one_hour' && type !== 'same_day';
  }

  /**
   * A localized copy template from the progress bar's data-bp-* attributes
   * (snippets/bundle-progress-bar.liquid), with its `{{ name }}` placeholders filled.
   * Attribute values are already entity-decoded by the DOM; decodeTranslatedText covers
   * a double-escaped `t` value. Placeholders with no matching var are left visible.
   * @param {string} attr dataset key, e.g. 'bpTextDone'
   * @param {string} fallback English template
   * @param {Record<string, string|number>} [vars]
   * @returns {string}
   */
  #progressCopy(attr, fallback, vars = {}) {
    const el = this.querySelector('.bundle-progress');
    const template = decodeTranslatedText(el?.dataset[attr] || '') || fallback;
    return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, name) => (name in vars ? String(vars[name]) : match));
  }

  /**
   * Render progress dots + labels from the discount config's tiers. Free delivery is
   * NOT a milestone — it only drives the "free delivery unlocked" prefix in
   * #updateProgress.
   */
  #renderProgressMarkers() {
    const markers = this.#discountConfig.tiers
      .filter((t) => t.threshold > 0)
      .map((t) => ({
        threshold: t.threshold,
        label:
          t.type === 'fixed'
            ? this.#progressCopy('bpTierFixed', '{{ amount }} off', {
                // Whole-dollar amounts drop the trailing zeros, like Liquid's money_without_trailing_zeros.
                amount: this.#formatMoney(Math.round(t.value * 100)).replace(/[.,]00(?!\d)/, ''),
              })
            : this.#progressCopy('bpTierPercent', '{{ value }}% off', { value: t.value }),
      }));
    // Every tier is measured on the same (undiscounted) selection subtotal, so the raw
    // threshold is the correct sort key.
    markers.sort((a, b) => a.threshold - b.threshold);
    this.#markers = markers;
    this.#maxThreshold = markers.length ? markers[markers.length - 1].threshold : 0;

    const bar = this.querySelector('.bundle-progress__bar');
    const labelsWrap = this.querySelector('.bundle-progress__labels');
    if (!bar || !labelsWrap) return;

    for (const dot of bar.querySelectorAll('.bundle-progress__dot')) dot.remove();
    labelsWrap.innerHTML = '';
    const icon = bar.querySelector('.bundle-progress__icon');

    if (markers.length === 0) {
      if (icon) icon.hidden = true;
      return;
    }

    // Solid start dot at the origin.
    const startDot = document.createElement('span');
    startDot.className = 'bundle-progress__dot bundle-progress__dot--start';
    startDot.style.left = '0%';
    bar.insertBefore(startDot, icon || null);

    const lastIndex = markers.length - 1;
    markers.forEach((m, i) => {
      // Evenly space markers across the bar (equal gap from the start dot at 0%
      // through to the last marker at 100%), independent of threshold magnitude.
      const pct = ((i + 1) / markers.length) * 100;
      const isLast = i === lastIndex;

      // The final (highest) marker is the strawberry; earlier markers are dots.
      if (isLast && icon) {
        icon.style.insetInlineStart = `${pct}%`;
        icon.hidden = false;
      } else {
        const dot = document.createElement('span');
        dot.className = 'bundle-progress__dot';
        dot.style.left = `${pct}%`;
        dot.dataset.threshold = String(m.threshold);
        bar.insertBefore(dot, icon || null);
      }

      const label = document.createElement('span');
      label.className = 'bundle-progress__label';
      label.style.left = `${pct}%`;
      label.textContent = m.label;
      // Center each label under its marker, EXCEPT the last (at 100%, the right edge):
      // centering spills it out of the drawer, so right-align it (-100%) to end under
      // the marker. (CSS default is translateX(-50%).)
      label.style.transform = isLast ? 'translateX(-100%)' : 'translateX(-50%)';
      labelsWrap.appendChild(label);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#handleDocumentClick);
    document.removeEventListener('keydown', this.#handleKeyDown);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener('store:availability:updated', this.#handleStoreAvailabilityChange);
    document.removeEventListener('store-availability:change', this.#handleLocatorSelectionChange);
    this.removeEventListener('click', this.#onFooterInputClick, true);
    removeTrapFocus();
    delete window.openBundleBuilderDrawer;
    delete window.closeBundleBuilderDrawer;
    delete window.bundleBuilderAddProduct;
    delete window.bundleBuilderWarm;
  }

  /** @param {MouseEvent} event */
  #handleDocumentClick = (event) => {
    const trigger = event.target.closest('[data-bundle-builder-trigger]');
    if (trigger) {
      event.preventDefault();
      event.stopPropagation();
      this.open();
    }
  };

  /** @param {KeyboardEvent} event */
  #handleKeyDown = (event) => {
    if (event.key === 'Escape' && this.#isOpen) {
      event.preventDefault();
      this.close();
    }
  };

  /** Bind document-level click listener for trigger buttons */
  #bindTriggers() {
    document.addEventListener('click', this.#handleDocumentClick);
  }

  /** Open the drawer */
  open() {
    if (this.#isOpen) return;
    // First open: build the contents and run the setup that was deferred with them.
    this.#hydrate();
    this.#isOpen = true;
    // Expose the open state on the root so the shared store selector can detect a
    // Bundle Builder context from any page and hide the 1-Hour delivery option
    // (BYOB can't be fulfilled in 1-Hour). See store-availability.js#bbHidesOneHour.
    this.setAttribute('data-bb-open', '');

    // Cancel any pending close-hide from a recent close() so it can't hide the
    // drawer we're about to show (the "drawer disappears" bug on quick reopen).
    if (this.#closeTimer) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }

    // Save the trigger element so focus can be restored on close
    this.#triggerElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const overlay = this.refs.overlay;
    const drawer = this.refs.drawer;

    overlay.style.display = '';
    drawer.style.display = '';
    drawer.setAttribute('aria-hidden', 'false');

    // Force reflow before adding class
    void drawer.offsetHeight;

    requestAnimationFrame(() => {
      overlay.classList.add('bb-overlay--open');
      drawer.classList.add('bb-drawer--open');
      trapFocus(drawer);
    });

    hideZendeskLauncher(ZENDESK_HIDE_SOURCE);

    // Lock body scroll
    document.body.style.overflow = 'hidden';

    this.#updateProgress();
    this.#updateFooter();
    // Reconcile with the cart in case it changed since last open.
    this.#syncFromCart();
  }

  /** Close the drawer */
  close() {
    if (!this.#isOpen) return;
    this.#isOpen = false;
    this.removeAttribute('data-bb-open');

    removeTrapFocus();
    this.#closeSecondary(true);
    // The gate can raise the locator directly on the primary drawer (secondary never
    // opened), and #closeSecondary's early-return skips #hideLocator in that case — so
    // reset it here too, or a reopen lands on a stale, still-inert locator.
    this.#hideLocator();

    const overlay = this.refs.overlay;
    const drawer = this.refs.drawer;

    overlay.classList.remove('bb-overlay--open');
    drawer.classList.remove('bb-drawer--open');
    drawer.setAttribute('aria-hidden', 'true');

    // Hide only if the drawer is STILL closed when these fire — a reopen in the
    // meantime sets #isOpen back to true, so the stale hide is skipped.
    const onEnd = () => {
      drawer.removeEventListener('transitionend', onEnd);
      if (this.#isOpen) return;
      overlay.style.display = 'none';
      drawer.style.display = 'none';
    };
    drawer.addEventListener('transitionend', onEnd, { once: true });

    // Fallback if transition doesn't fire
    this.#closeTimer = setTimeout(() => {
      this.#closeTimer = null;
      if (this.#isOpen) return;
      overlay.style.display = 'none';
      drawer.style.display = 'none';
    }, 400);

    // Unlock body scroll
    document.body.style.overflow = '';

    restoreZendeskLauncher(ZENDESK_HIDE_SOURCE);

    // Restore focus to the element that triggered the drawer
    if (this.#triggerElement) {
      this.#triggerElement.focus();
      this.#triggerElement = null;
    }
  }

  /** Handle close button click */
  handleClose(event) {
    event.preventDefault();
    this.close();
  }

  /** Handle overlay (backdrop) click. The on:click binding is delegated on
   *  document, so event.currentTarget is document — compare against the overlay
   *  ref instead (the proxied event.target is the overlay node). */
  handleOverlayClick(event) {
    if (event.target === this.refs.overlay) {
      this.close();
    }
  }

  /** Initialize accordion step headers */
  #initAccordions() {
    const stepHeaders = this.querySelectorAll('.bb-step__header[data-collapsible="true"]');
    for (const header of stepHeaders) {
      header.addEventListener('click', () => {
        const step = header.closest('.bb-step');
        const body = step.querySelector('.bb-step__body');
        const chevron = header.querySelector('.bb-step__chevron');
        const isExpanded = header.getAttribute('aria-expanded') === 'true';

        header.setAttribute('aria-expanded', String(!isExpanded));
        body.hidden = isExpanded;

        if (chevron) {
          chevron.style.transform = isExpanded ? 'rotate(0deg)' : 'rotate(180deg)';
        }
      });
    }
  }

  /** Initialize tab switching (show/hide pre-rendered panels) */
  #initTabs() {
    const tabs = this.querySelectorAll('.bb-tab');
    for (const tab of tabs) {
      tab.addEventListener('click', () => {
        const step = tab.closest('.bb-step');
        const tabContainer = tab.closest('.bb-tabs');
        const allTabs = tabContainer.querySelectorAll('.bb-tab');

        // Update tab states
        for (const t of allTabs) {
          t.dataset.active = 'false';
          t.classList.remove('bb-tab--active');
        }
        tab.dataset.active = 'true';
        tab.classList.add('bb-tab--active');

        // Toggle the matching product panel within this step
        const tabName = tab.dataset.tab;
        const panels = step.querySelectorAll('.bb-products__panel');
        for (const panel of panels) {
          panel.hidden = panel.dataset.tabPanel !== tabName;
        }
      });
    }
  }

  /** Bind Add handlers on all pre-rendered product cards */
  #bindCardButtons() {
    const buttons = this.querySelectorAll('.bb-card__action');
    for (const btn of buttons) {
      btn.addEventListener('click', this.#handleCardButtonClick);
    }
  }

  /** Parse JSON dataset value, returning a fallback on error. */
  #parseJSON(value, fallback) {
    try {
      return JSON.parse(value);
    } catch {
      return fallback;
    }
  }

  /** @param {MouseEvent} event */
  #handleCardButtonClick = (event) => {
    event.preventDefault();
    this.#triggerCardAdd(event.currentTarget);
  };

  /**
   * Run the Add decision for a product card's button — used by the card's own
   * click and by external page product cards (window.bundleBuilderAddProduct).
   * @param {HTMLElement} btn
   */
  #triggerCardAdd(btn) {
    if (!btn) return;
    const card = btn.closest('.bb-card');
    if (!card) return;
    // Known unavailable for the current store/delivery — don't open or add.
    if (this.#unavailableProducts.has(Number(btn.dataset.productId))) return;

    // Build the picker from the BUNDLE-ELIGIBLE variant set only (data-variants);
    // non-eligible sizes are never shown and their external ids are never sent to
    // the availability API. Fall back to a single fabricated variant when the card
    // carries no eligible-variant list (a single-/default-variant product).
    const eligibleVariants = this.#parseJSON(btn.dataset.variants, []);
    let pickerVariants = Array.isArray(eligibleVariants) && eligibleVariants.length ? eligibleVariants : [];

    if (!pickerVariants.length) {
      pickerVariants = [
        {
          id: Number(btn.dataset.variantId),
          // Real variant title so the picker shows the size (not the product name);
          // a genuine single-variant product carries "Default Title" → falls back
          // to the product title in #renderSecondaryVariants, as before.
          title: btn.dataset.variantTitle || 'Default Title',
          price: Number(btn.dataset.price),
          compare: Number(btn.dataset.comparePrice || 0),
          available: true,
          externalVariantId: btn.dataset.externalVariantId || '',
          image: btn.dataset.image || '',
          delivery: btn.dataset.delivery || '',
          deliveryIcon: btn.dataset.deliveryIcon || '',
        },
      ];
    }

    const ctx = {
      card,
      productId: Number(btn.dataset.productId),
      title: btn.dataset.title || '',
      image: btn.dataset.image || '',
      handle: btn.dataset.handle || '',
      badge: btn.dataset.badge || '',
      isAddon: btn.dataset.addon === 'true',
      // Primary vs secondary is driven purely by seo.hidden (== 1 → secondary).
      // A bundle must contain at least one primary product to be orderable.
      seoHidden: card.dataset.seoHidden === 'true',
      wrapAvailable: btn.dataset.wrapAvailable === 'true',
      variants: pickerVariants,
    };

    // No usable store selection yet — none chosen, or the zip was cleared from the header
    // or the footer (both leave #hasStoreSelected false). Ask for one on the PRIMARY drawer
    // BEFORE adding anything, then resume this exact Add once a store lands. Covers both the
    // single- and multi-variant paths and both entry points (card click + addProductById).
    // #secondaryCtx is set first so the locator gate narrows the store list to THIS product
    // (EA-3985), exactly as #openSecondary would.
    if (!this.#hasStoreSelected()) {
      this.#secondaryCtx = ctx;
      this.#pendingAddBtn = btn;
      this.open();
      this.#showLocator();
      return;
    }

    // ONE bundle-eligible variant → there is no size to choose, so DON'T open the
    // secondary picker: show a loader on the Add button, run the per-variant
    // fulfillment availability check, and add straight to the bundle if it passes
    // (mark the product unavailable if it fails). MORE than one → open the picker as
    // before. `data-multi-variant` is is_multi (eligible_count > 1) from
    // snippets/bb-product-card.liquid; the length guard is belt-and-braces.
    if (btn.dataset.multiVariant !== 'true' || pickerVariants.length <= 1) {
      this.#addSingleVariantDirect(btn, ctx);
      return;
    }

    this.#openSecondary(ctx);
  }

  /**
   * Add a single-variant product to the bundle. Fast-adds (no picker) when the store/time
   * fulfilment is ALREADY KNOWN available from the (possibly prefetched) cache; otherwise
   * opens the secondary picker, which owns every async state — loader while v4 resolves,
   * variant / unavailable (2.2 → change store) / error (2.1 → Try again). It NEVER fails
   * open: a shopper can't add on unknown availability (EA-3873 PR-2).
   * @param {HTMLElement} btn the card's Add button
   * @param {object} ctx the same context #openSecondary builds
   */
  #addSingleVariantDirect(btn, ctx) {
    const variant = ctx.variants[0];
    if (!variant) return;
    // Secondary (BYOB add-on) and primary alike are gated on the cached v4 caps below. The
    // drawer/cart card filter (bb-secondary-availability.js) does NOT hide secondary cards, so
    // the ONLY place their fulfilment is enforced is here at Add. #warmSecondary pre-warms one
    // batched v4 for every secondary product into #capsByContext, so this reads that answer with
    // no extra call — the drawer/cart no longer adds an unfulfillable secondary item the BB page
    // grid would hide (EA-3873).
    // Shipment is NOT auto-fulfillable any more — not every product ships, so it's gated by
    // v4's shipment[] like delivery/pickup (EA-3873). It flows through the same cache/gate below;
    // #availabilityContext returns a shipment context (no storeId).
    const ctxInfo = this.#availabilityContext();
    const extId = this.#variantExternalId(variant);
    // Nothing to gate against (no store / no external id). "No store" is already handled by
    // the store gate upstream (#triggerCardAdd), so here it means ungateable → allow.
    if (!ctxInfo || extId == null) {
      this.#commitBundleItem(ctx, variant);
      return;
    }
    // Already resolved for this store from the prefetch/cache → act now, no picker.
    const cached = this.#capsByContext.get(ctxInfo.key);
    if (cached && cached.has(extId)) {
      const c = cached.get(extId);
      if (c && c[ctxInfo.fulfillmentType] === true) this.#commitBundleItem(ctx, variant);
      else this.#openSecondary(ctx); // known unavailable → 2.2 (change store / time)
      return;
    }
    // Not resolved yet — a background prefetch may be in flight, or none started. Open the
    // picker: it shows a loader (sharing the in-flight call via #ensureCardCaps, or starting
    // one now — we pull this card from the queue first), then the variant / 2.2 / 2.1.
    this.#dequeuePrefetch(ctx.productId);
    this.#openSecondary(ctx);
  }


  /**
   * The raw arrangement_id metafield ("US-4567") for a product in the drawer, read
   * from its card (`.bb-card[data-arrangement-id]`). '' when the card is not rendered
   * or the product has no arrangement id.
   * @param {number} productId
   * @returns {string}
   */
  #arrangementIdForProduct(productId) {
    const card = this.querySelector(`.bb-card[data-product-id="${productId}"]`);
    return card?.getAttribute('data-arrangement-id')?.trim() || '';
  }

  /**
   * Add (or replace) a product's bundle line item in the draft from a resolved variant.
   * Shared by the secondary picker (handleSecondaryAdd) and the single-variant direct
   * add so both stamp the line identically; callers own any drawer close/animation.
   * @param {object} ctx
   * @param {object} variant
   */
  #commitBundleItem(ctx, variant) {
    const variantTitle = variant.title && variant.title !== 'Default Title' ? variant.title : '';
    this.#removeByProduct(ctx.productId);
    this.#addItem({
      variantId: Number(variant.id),
      productId: ctx.productId,
      title: ctx.title,
      image: ctx.image,
      price: Number(variant.price),
      compareAtPrice: Number(variant.compare || 0),
      handle: ctx.handle,
      badge: ctx.badge,
      variantTitle,
      externalVariantId: variant.externalVariantId || '',
      quantity: 1,
      isAddon: ctx.isAddon || false,
      seoHidden: ctx.seoHidden === true,
      draft: true,
      // Newest add → highest order → sorts to index 0 (#reorderCardsByBundle). Re-selecting a
      // variant re-adds it (removeByProduct + addItem), so it correctly jumps back to front.
      addOrder: ++this.#addOrderCounter,
    });
    this.#applyCardState(ctx.productId);
    this.#reorderCardsByBundle();
    this.#scrollProductPanelsToStart(ctx.productId);
    this.#updateProgress();
    this.#updateFooter();
    this.#saveState();
  }

  /**
   * Ensure every selected item carries a numeric `addOrder`, absorbing existing ones into
   * the counter first (so restored/synced maxes are respected) then filling any gaps in
   * array order. Restore preserves the persisted order; cart-sync (no stored order) gets a
   * stable order from cart position. Keeps new adds climbing above everything restored.
   * @param {Array<object>} items
   */
  #normalizeAddOrders(items) {
    for (const it of items) {
      if (typeof it.addOrder === 'number' && isFinite(it.addOrder) && it.addOrder > this.#addOrderCounter) {
        this.#addOrderCounter = it.addOrder;
      }
    }
    for (const it of items) {
      if (typeof it.addOrder !== 'number' || !isFinite(it.addOrder)) {
        it.addOrder = ++this.#addOrderCounter;
      }
    }
  }

  /**
   * Reorder every accordion's cards so products already in the bundle come FIRST, newest
   * (highest addOrder) at index 0, and everything else keeps its server order behind them
   * (EA-3873, point 6). Applies to BOTH the primary (Step 1) and secondary (Step 2) panels
   * — `.bb-products__panel` covers both. Idempotent (stable), so it can run on every load,
   * store change and add without thrashing. A trailing empty-state `<p>` (if any) stays last.
   */
  #reorderCardsByBundle() {
    const orderByProduct = new Map();
    for (const item of this.#selectedItems) {
      orderByProduct.set(Number(item.productId), typeof item.addOrder === 'number' ? item.addOrder : 0);
    }
    for (const panel of this.querySelectorAll('.bb-products__panel')) {
      const cards = [...panel.querySelectorAll(':scope > .bb-card')];
      if (cards.length < 2) continue;
      const added = [];
      const rest = [];
      for (const card of cards) {
        if (orderByProduct.has(Number(card.dataset.productId))) added.push(card);
        else rest.push(card);
      }
      if (!added.length) continue;
      added.sort(
        (a, b) => orderByProduct.get(Number(b.dataset.productId)) - orderByProduct.get(Number(a.dataset.productId)),
      );
      const frag = document.createDocumentFragment();
      for (const card of [...added, ...rest]) frag.appendChild(card);
      panel.insertBefore(frag, panel.firstChild);
    }
  }

  /**
   * Scroll a product's panel(s) to the leftmost so a just-added, now-front card is visible.
   * @param {number} productId
   */
  #scrollProductPanelsToStart(productId) {
    for (const card of this.querySelectorAll(`.bb-card[data-product-id="${productId}"]`)) {
      const panel = card.closest('.bb-products__panel');
      if (panel) panel.scrollTo({ left: 0, behavior: 'smooth' });
    }
  }

  /**
   * Toggle the spinner/disabled loading state on a card's Add button while a
   * single-variant direct add's availability check is in flight.
   * @param {HTMLElement} btn
   * @param {boolean} on
   */
  #setCardAddLoading(btn, on) {
    if (!btn) return;
    btn.classList.toggle('bb-card__action--loading', on);
    btn.disabled = on;
    if (on) btn.setAttribute('aria-busy', 'true');
    else btn.removeAttribute('aria-busy');
  }

  /**
   * Open the drawer and start the Add flow for a product (used by page product
   * cards on the bundle-builder page). Routes through the same decision logic:
   * every product opens the secondary drawer so the availability API runs first.
   * @param {number|string} productId
   */
  addProductById(productId) {
    this.open();
    const id = Number(productId);
    // Known unavailable for the current store/delivery — surface the drawer but
    // don't add (the disabled card CTA already reflects this).
    if (this.#unavailableProducts.has(id)) return;
    const inSelected = this.#selectedItems.some((i) => i.productId === id);
    // Already in the current draft — just surface the drawer, don't re-add.
    if (inSelected) return;
    const btn = this.querySelector(`.bb-card[data-product-id="${id}"] .bb-card__action`);
    if (btn) {
      this.#activateCardTab(btn);
      this.#triggerCardAdd(btn);
    }
  }

  /**
   * Switch the drawer to the tab/step that holds a given card so the shopper
   * sees it after an external trigger.
   * @param {HTMLElement} btn
   */
  #activateCardTab(btn) {
    const panel = btn.closest('.bb-products__panel');
    if (!panel) return;
    const tabName = panel.dataset.tabPanel;
    const step = panel.closest('.bb-step');
    if (!step || tabName == null) return;
    const tab = step.querySelector(`.bb-tab[data-tab="${CSS.escape(tabName)}"]`);
    if (tab) tab.click();
  }

  // ── Secondary drawer (variant picker) ────────────────────────────

  /** Open the secondary drawer for a product. @param {object} ctx */
  #openSecondary(ctx) {
    this.#secondaryCtx = ctx;

    // Pre-select the default variant (first available, else first) — for both
    // single- and multi-variant products — so the Add CTA is enabled by default.
    const defaultVariant = ctx.variants.find((v) => v.available) || ctx.variants[0];
    ctx.selectedVariantId = defaultVariant ? defaultVariant.id : null;

    const titleEl = this.querySelector('[data-bb2-title]');
    if (titleEl) titleEl.textContent = this.dataset.variantSelectTitle || 'Select your size';

    const nameEl = this.querySelector('[data-bb2-product-name]');
    if (nameEl) nameEl.textContent = ctx.title;

    // Gift wrap is a variant now — only the framing subtitle + Learn More remain,
    // revealed when the product opts in via `wrap_available` (mirrors cart/PD).
    // Set BEFORE the render below, whose unavailable state hides it again: set after,
    // a warm-cache "not available" render showed the blurb under the message.
    const wrapSection = this.querySelector('[data-bb2-giftwrap]');
    if (wrapSection) wrapSection.hidden = !ctx.wrapAvailable;

    // Render the variant tiles. #applyVariantAvailability renders the final
    // (gated) list synchronously when availability is already warm, or a loader
    // while it resolves — its synchronous portion runs before the drawer paints.
    this.#applyVariantAvailability(ctx);

    this.#updateSecondaryAddState();

    const overlay = this.refs.secondaryOverlay;
    const drawer = this.refs.secondaryDrawer;
    overlay.style.display = '';
    drawer.style.display = '';
    void drawer.offsetHeight;
    requestAnimationFrame(() => {
      overlay.classList.add('bb2-overlay--open');
      drawer.classList.add('bb2-drawer--open');
      // Move the focus trap to the secondary panel while it's open.
      trapFocus(drawer);
    });

    /* Store selection is asked for earlier now — at the Add click, on the PRIMARY drawer
       (see #triggerCardAdd's gate) — so a usable one is guaranteed by the time the picker
       opens. A shopper who wants to change it reaches the selector through the "not
       available" state's "Change store or delivery time" button (#renderSecondaryUnavailable). */
  }

  /* ── Embedded store selector ──────────────────────────────────────────────
   *
   * A second `<store-availability-component>` rendered by this section (see
   * sections/ea-bundle-builder-drawer.liquid). Nothing about its rules is
   * reimplemented here — this decides WHEN it is shown, reacts to what it saves, and
   * hands it a `storeResultsGate` for the product whose picker it covers.
   *
   * The gate is the product question the store lookup cannot ask (EA-3985). That
   * lookup takes zip, date and fulfillment type only, so for zip 30041 it offers — and
   * auto-selects — store 1056, which delivers, just not 11706/11620; 770 does. The
   * gate narrows the list to the stores a store-less v4 answer names for this product,
   * the same intersection the PDP's Change Store list and the personalization drawer
   * show. Nothing is switched for the shopper: the selector still needs its own CTA.
   */

  /** True while the selector is showing over the variant picker. */
  #locatorActive = false;

  /** @type {AbortController|null} — scroll listener for the embedded store-list bottom fade. */
  #fadeScrollAC = null;
  /** @type {ResizeObserver|null} — re-checks overflow when the list or panel resizes. */
  #fadeResizeObserver = null;

  /**
   * The card Add button whose Add was intercepted by the store gate, to resume once a
   * store lands. Set by #triggerCardAdd (and the secondary's "Change store or delivery
   * time"); consumed by #handleLocatorSelectionChange; cleared by #hideLocator.
   * @type {HTMLElement|null}
   */
  #pendingAddBtn = null;

  /**
   * Footer zip / delivery-date field clicked → open the EMBEDDED locator on this drawer,
   * not the header pop-up. Capture phase + stopPropagation pre-empts store-availability.js's
   * bubble-phase handler for the same [data-store-availability-trigger]. The clear (×) button
   * keeps its normal clear-the-store behavior. No product context → the store list isn't
   * narrowed (a general "change store", EA-3873).
   * @param {MouseEvent} event
   */
  #onFooterInputClick = (event) => {
    const inputs = event.target.closest('.bb-footer__inputs');
    if (!inputs || !this.contains(inputs)) return;
    // Let the clear (×) button clear the selection as before.
    if (event.target.closest('[data-clear-store]')) return;
    event.preventDefault();
    event.stopPropagation();
    // General store change (not tied to a product add) → no gating context, no pending add.
    this.#secondaryCtx = null;
    this.#pendingAddBtn = null;
    this.#showLocator();
  };

  /** The embedded selector element, or null on a page that rendered none. */
  #locatorComponent() {
    return this.querySelector('store-availability-component[data-embedded]');
  }

  /** Its close control, pinned to the card's top-right corner. */
  #locatorClose() {
    return this.querySelector('[data-bb-locator-close]');
  }

  /**
   * The zip the shopper has already committed to, or null.
   *
   * Deliberately narrower than #hasStoreSelected: that answers "can we proceed", this
   * answers "is there anything behind the selector to go back to" — and a zip is the
   * whole of it, because the variant list behind was resolved against it.
   * @returns {string|null}
   */
  #savedZipCode() {
    const state = this.#getStoreState();
    const zip = state && typeof state.zipCode === 'string' ? state.zipCode.trim() : '';
    return zip || null;
  }

  /**
   * Swap the drawer header title between the bundle heading ('default' → "Build your
   * bundle") and the store-locator heading ('availability' → "Check availability").
   * Both strings are baked into data-title-* attrs on .bb-header__title from the
   * section settings. Mirrors the personalization drawer's #setHeaderTitle.
   * @param {'default'|'availability'} mode
   */
  #setHeaderTitle(mode) {
    const el = this.refs.drawer?.querySelector('.bb-header__title');
    if (!el) return;
    const t = el.getAttribute(`data-title-${mode}`);
    if (t) el.textContent = t;
  }

  /**
   * Offset the embedded locator panel below the header. The header (.bb-top) paints
   * above the panel (z-index 6 > 5), so without this its bottom shadow would sit over
   * the panel and the card would scroll up behind it. Measured here because the header
   * height varies with the progress bar; mirrors the PDrawer's --pd-header-height.
   */
  #syncLocatorOffset() {
    const drawer = this.refs.drawer;
    const top = drawer?.querySelector('.bb-top');
    if (drawer && top) drawer.style.setProperty('--bb-top-height', `${top.offsetHeight}px`);
  }

  /**
   * Show the selector over the variant picker.
   *
   * `whenDefined` is not optional: the panel's markup is in the DOM from page load, so
   * before its class is registered the element is an unupgraded HTMLElement and
   * anything assigned to it would be wiped by the class field initialisers at upgrade.
   * @returns {Promise<boolean>} false when this page rendered no selector.
   */
  async #showLocator() {
    const panel = this.refs.locatorPanel;
    const selector = this.#locatorComponent();
    if (!panel || !selector) return false;
    if (this.#locatorActive) return true;

    this.#locatorActive = true;
    panel.hidden = false;

    // Flip the header title to "Check availability" while the selector is up (mirrors the
    // personalization drawer), and offset the panel below the header so the pink header +
    // its flipped title stay visible. The header is NOT inert (its close stays live).
    this.#setHeaderTitle('availability');
    this.#syncLocatorOffset();

    // Always dismissible on the primary drawer: unlike the old secondary placement (whose
    // back button was the way out on a first-time gate), this panel has no button behind
    // it, so its own close IS the way out. Dismissing with no store lands on the drawer
    // with its ATC disabled ("Select a store"); the next Add re-gates — never a dead end.
    const close = this.#locatorClose();
    if (close) close.hidden = false;

    this.#setBehindLocatorInert(true);

    await customElements.whenDefined('store-availability-component');
    if (!this.#locatorActive) return true; // a selection landed while we waited

    // Before openEmbedded, whose first lookup would otherwise render ungated. And after
    // whenDefined: assigned to an unupgraded element it is wiped by the class field.
    selector.storeResultsGate = this.#locatorGate;
    if (typeof selector.openEmbedded === 'function') selector.openEmbedded('bundle-builder-drawer');
    // Bottom gradient fade over the store list — signals more stores below (the embedded component
    // leaves the fade to its host, mirroring the PDrawer).
    this.#attachFadeScroll();
    return true;
  }

  /** The contract the selector calls back on — see `storeResultsGate` in store-availability.js. */
  #locatorGate = {
    onLookupStart: (ctx) => {
      this.#startLocatorProbe(ctx);
    },
    resolve: (ctx) => this.#resolveLocatorGate(ctx),
  };

  /** @type {{key: string, promise: Promise<object|null>}|null} The v4 question for the selector's current zip/date. */
  #locatorProbe = null;

  /** EA ids of the sizes the open picker offers — the product the selector is gating for. */
  #secondaryProductIds() {
    const ctx = this.#secondaryCtx;
    if (!ctx || !Array.isArray(ctx.variants)) return [];
    const ids = new Set();
    for (const v of ctx.variants) {
      const id = this.#variantExternalId(v);
      if (id != null) ids.add(id);
    }
    return [...ids];
  }

  /**
   * Start (or reuse) the store-less v4 check for the selector's zip and date, in
   * parallel with its own store lookup. Keyed without the fulfillment type: one v4
   * answer carries delivery and pickup both, so the toggle re-reads it.
   * @param {object} ctx the selector's lookup context
   * @returns {Promise<object|null>|null} null when there is nothing to ask
   */
  #startLocatorProbe(ctx) {
    const productIds = this.#secondaryProductIds();
    if (!productIds.length || !ctx.zipCode || !ctx.fulfillmentDate) {
      this.#locatorProbe = null;
      return null;
    }
    const key = `${String(ctx.country).toUpperCase()}|${ctx.zipCode}|${ctx.fulfillmentDate}|${productIds.join(',')}`;
    if (this.#locatorProbe && this.#locatorProbe.key === key) return this.#locatorProbe.promise;
    const promise = fetchProductAvailability({
      productIds,
      date: ctx.fulfillmentDate,
      zipCode: ctx.zipCode,
      country: ctx.country,
    }).catch(() => null);
    this.#locatorProbe = { key, promise };
    return promise;
  }

  /**
   * Which of the selector's stores can make this product.
   *
   *   stores can make it     → list narrowed to them
   *   none, for this date    → the list is replaced by the "try another store or
   *                            delivery time" message; the date controls stay live
   *
   * Fails OPEN — the unnarrowed list, as before — on shipment (sellable nationwide,
   * see #isShipment), nothing to ask, or a check that could not answer: the per-size
   * check that runs once a store is saved still stands behind it.
   * @param {object} ctx the selector's lookup context
   * @returns {Promise<{ok: boolean, message?: string, eligibleStoreIds?: Set<string>, ifNoneNearby?: object}>}
   */
  async #resolveLocatorGate(ctx) {
    if (ctx.shipmentOnly || ctx.fulfillmentType === 'shipment') return { ok: true };
    const productIds = this.#secondaryProductIds();
    const promise = this.#startLocatorProbe(ctx);
    if (!promise) return { ok: true };
    const data = await promise;
    if (!data) return { ok: true };

    const type = ctx.requestedFulfillmentType === 'pickup' ? 'pickup' : 'delivery';
    const eligible = eligibleStoreIdsFor(data, type, productIds, { oneHourOnly: ctx.deliveryType === 'one_hour' });
    if (eligible === null) return { ok: true };

    const message = this.#unavailableMessage(type);
    if (eligible.size === 0) return { ok: false, message };
    return { ok: true, eligibleStoreIds: eligible, ifNoneNearby: { message } };
  }

  /**
   * "Sorry, this product is not available. Please try another store or delivery time."
   * — the PDP / personalization drawer copy, from the section's data attributes.
   * @param {'delivery'|'pickup'} fulfillmentType
   * @returns {string}
   */
  #unavailableMessage(fulfillmentType) {
    if (fulfillmentType === 'pickup') {
      return this.dataset.unavailablePickupText || 'Sorry, this product is not available. Please try another store or pickup time.';
    }
    return this.dataset.unavailableDeliveryText || 'Sorry, this product is not available. Please try another store or delivery time.';
  }

  /** Put the selector away and hand the variant picker back. */
  #hideLocator() {
    if (!this.#locatorActive) return;
    this.#locatorActive = false;
    // Store chosen (or panel dismissed) → back to the "Build your bundle" title.
    this.#setHeaderTitle('default');
    this.#detachFadeScroll();
    // A dismiss (close button / drawer close) cancels any Add that was waiting on a store;
    // a selection captures the button BEFORE calling here, so its resume still fires.
    this.#pendingAddBtn = null;
    if (this.refs.locatorPanel) this.refs.locatorPanel.hidden = true;
    const close = this.#locatorClose();
    if (close) close.hidden = true;
    this.#setBehindLocatorInert(false);
    const selector = this.#locatorComponent();
    // Released so the gate never outlives the picker it was answering for.
    if (selector) selector.storeResultsGate = null;
    this.#locatorProbe = null;
    // Its dropdown or calendar may be open; hiding the panel around them would leave
    // them open behind it, so the next open lands on a menu nobody asked for.
    if (selector && typeof selector.closeEmbedded === 'function') selector.closeEmbedded();
  }

  /**
   * Show or hide the gradient fade at the bottom of the embedded store list. The fade only appears
   * when the list actually OVERFLOWS its viewport (more cards than fit) AND isn't scrolled to the
   * very bottom — its sole purpose is to signal there are more cards below. Mirrors the PDrawer's,
   * since the embedded store-availability-component leaves the fade to its host.
   */
  #updateStoresFade() {
    const selector = this.#locatorComponent();
    if (!selector) return;
    const scroll = selector.querySelector('.store-availability-dialog__stores-scroll');
    const fade = selector.querySelector('.store-availability-dialog__stores-fade');
    if (!scroll || !fade) return;
    // A 1px cushion absorbs sub-pixel rounding so a perfectly-fitting list never flickers the fade on.
    const canScroll = scroll.scrollHeight - scroll.clientHeight > 1;
    const atBottom = scroll.scrollHeight - scroll.scrollTop - scroll.clientHeight < 2;
    fade.hidden = !canScroll || atBottom;
  }

  /** Wire the scroll + resize listeners for the embedded store-list fade. */
  #attachFadeScroll() {
    this.#detachFadeScroll();
    const selector = this.#locatorComponent();
    if (!selector) return;
    const scroll = selector.querySelector('.store-availability-dialog__stores-scroll');
    if (!scroll) return;
    this.#fadeScrollAC = new AbortController();
    const opts = { signal: this.#fadeScrollAC.signal };
    scroll.addEventListener('scroll', () => this.#updateStoresFade(), { passive: true, ...opts });
    // Re-evaluate after store results arrive (cards added/removed). The component emits on document.
    document.addEventListener(
      'store-availability:change',
      () => requestAnimationFrame(() => this.#updateStoresFade()),
      opts,
    );
    // Layout settles a frame or two after cards render; a ResizeObserver re-checks overflow on every
    // size change (cards in/out, panel resize) so the fade never lingers over a short list.
    if ('ResizeObserver' in window) {
      this.#fadeResizeObserver = new ResizeObserver(() => this.#updateStoresFade());
      this.#fadeResizeObserver.observe(scroll);
      const stores = scroll.querySelector('.store-availability-dialog__stores');
      if (stores) this.#fadeResizeObserver.observe(stores);
    }
    // Initial state — may already have store cards.
    this.#updateStoresFade();
  }

  /** Tear down the fade listeners (locator hidden / drawer close). */
  #detachFadeScroll() {
    if (this.#fadeScrollAC) {
      this.#fadeScrollAC.abort();
      this.#fadeScrollAC = null;
    }
    if (this.#fadeResizeObserver) {
      this.#fadeResizeObserver.disconnect();
      this.#fadeResizeObserver = null;
    }
  }

  /**
   * Take the variant picker BEHIND the selector out of play, or give it back.
   *
   * The scrim stops the pointer and dims the view, and that is all it does: a
   * translucent overlay leaves everything under it in the tab order and on the
   * accessibility tree, so a keyboard shopper would tab from the zip field straight
   * into the size buttons. `inert` is what actually removes them.
   *
   * Scoped to the primary drawer's main and footer — the content the locator overlays.
   * The header (.bb-top) is deliberately LEFT LIVE so its close button stays usable and
   * the flipped "Check availability" title stays visible above the panel (the locator
   * starts below the header, not over it).
   * @param {boolean} inert
   */
  #setBehindLocatorInert(inert) {
    const drawer = this.refs.drawer;
    if (!drawer) return;
    for (const region of [drawer.querySelector('.bb-main'), drawer.querySelector('.bb-footer')]) {
      if (region instanceof HTMLElement) region.toggleAttribute('inert', inert);
    }
  }

  /**
   * A selection landed — from this panel, or from the header while the drawer was
   * open. Either way, if it is usable the selector has done its job.
   */
  #handleLocatorSelectionChange = () => {
    if (!this.#locatorActive) return;
    // A clear, or a save that still leaves nothing usable, keeps the shopper here
    // rather than dropping them into a variant list that cannot resolve.
    if (!this.#hasStoreSelected()) return;
    // Capture the waiting Add BEFORE #hideLocator clears it, then resume it now that a
    // store is committed — the same button re-runs #triggerCardAdd, which (store now
    // present) routes to the single-variant add or opens the picker.
    const pending = this.#pendingAddBtn;
    this.#hideLocator();
    if (pending) this.#triggerCardAdd(pending);
  };

  /** Close the secondary drawer. @param {boolean} [immediate] */
  #closeSecondary(immediate = false) {
    const overlay = this.refs.secondaryOverlay;
    const drawer = this.refs.secondaryDrawer;
    if (!overlay || !drawer) return;
    if (drawer.style.display === 'none' || drawer.style.display === '') {
      if (!drawer.classList.contains('bb2-drawer--open')) {
        this.#secondaryCtx = null;
        return;
      }
    }

    overlay.classList.remove('bb2-overlay--open');
    drawer.classList.remove('bb2-drawer--open');
    this.#secondaryCtx = null;
    // Goes with the picker it covers — otherwise it is still up, and still holding the
    // content behind it inert, the next time the picker opens.
    this.#hideLocator();

    // Restore the focus trap to the main drawer (unless the whole drawer is
    // closing — close() has already called removeTrapFocus and set #isOpen false).
    if (this.#isOpen) trapFocus(this.refs.drawer);

    const hide = () => {
      overlay.style.display = 'none';
      drawer.style.display = 'none';
    };
    if (immediate) {
      hide();
      return;
    }
    drawer.addEventListener('transitionend', hide, { once: true });
    setTimeout(hide, 400);
  }

  /** Handle secondary drawer close/back */
  handleSecondaryClose(event) {
    event.preventDefault();
    this.#closeSecondary();
  }

  /** Handle secondary overlay (backdrop) click. See handleOverlayClick — compare
   *  against the ref, not event.currentTarget (which is document under delegation). */
  handleSecondaryOverlayClick(event) {
    if (event.target === this.refs.secondaryOverlay) {
      this.#closeSecondary();
    }
  }

  /** @param {MouseEvent} event */
  #handleSecondaryVariantClick = (event) => {
    const tile = event.currentTarget;
    if (tile.disabled || !this.#secondaryCtx) return;
    this.#secondaryCtx.selectedVariantId = Number(tile.dataset.variantId);
    const all = this.querySelectorAll('[data-bb2-variants] .bb2-variant');
    for (const t of all) {
      t.classList.toggle('bb2-variant--selected', t === tile);
    }
    this.#updateSecondaryAddState();
  };

  /**
   * Enable the secondary Add button only when NOT loading availability AND a
   * (fulfillable) variant is selected. Disabled while the API is in flight.
   */
  #updateSecondaryAddState() {
    const addBtn = this.querySelector('[data-bb2-add]');
    if (!addBtn) return;
    addBtn.disabled = this.#secondaryLoading || !(this.#secondaryCtx && this.#secondaryCtx.selectedVariantId);
  }

  /** Show/hide the secondary footer (the Add CTA row). */
  #setSecondaryFooterVisible(visible) {
    const footer = this.querySelector('.bb2-footer');
    if (footer) footer.style.display = visible ? '' : 'none';
  }

  /**
   * No variant is fulfillable for the selected method → replace the drawer body
   * with a centered "not available" message and HIDE the Add CTA entirely.
   *
   * The message names the way out, and the button under it takes it (EA-3985): it
   * opens the embedded selector gated to THIS product, so the stores offered are the
   * ones that can make it and the delivery-time controls are right there. Before, the
   * body said only "not available for delivery" and the shopper could only go back —
   * to a card this also disables, with the store change that would clear it nowhere
   * in sight. Under a cart lock the selector says so and offers Clear Cart itself.
   * @param {string} fulfillmentType - 'delivery' | 'pickup'
   */
  #renderSecondaryUnavailable(fulfillmentType) {
    this.#secondaryLoading = false;
    const type = fulfillmentType === 'pickup' ? 'pickup' : 'delivery';
    const variantsEl = this.querySelector('[data-bb2-variants]');
    const nameEl = this.querySelector('[data-bb2-product-name]');
    const gw = this.querySelector('[data-bb2-giftwrap]');
    if (nameEl) nameEl.style.display = 'none';
    if (gw) gw.hidden = true;
    if (variantsEl) {
      variantsEl.classList.add('bb2-variants--unavailable');
      const text = document.createElement('p');
      text.className = 'bb2-unavailable__text';
      text.textContent = this.#unavailableMessage(type);
      const change = document.createElement('button');
      change.type = 'button';
      change.className = 'bb2-unavailable__change';
      change.setAttribute('data-bb2-change-store', '');
      change.textContent = this.dataset.unavailableChangeText || 'Change store or delivery time';
      // The locator lives on the PRIMARY drawer now, behind this secondary picker — so
      // close the picker first, then raise it. Re-arm the gate for THIS product (ctx +
      // pending Add) so the list narrows to it (EA-3985) and the Add resumes on selection.
      change.addEventListener('click', () => {
        const ctx = this.#secondaryCtx;
        const btn = ctx ? this.querySelector(`.bb-card[data-product-id="${ctx.productId}"] .bb-card__action`) : null;
        this.#closeSecondary(true); // clears #secondaryCtx + #pendingAddBtn via #hideLocator
        this.#secondaryCtx = ctx;
        this.#pendingAddBtn = btn;
        this.#showLocator();
      });
      // "← Back" — return to the card grid (close the picker). Outlined #1d1d1d, hover-flips.
      const back = document.createElement('button');
      back.type = 'button';
      back.className = 'bb2-unavailable__back';
      back.setAttribute('data-bb2-back', '');
      back.textContent = this.dataset.unavailableBackText || '← Back';
      back.addEventListener('click', () => this.#closeSecondary());
      // Two CTAs on one line (stacked on mobile via CSS): Back, then select-alternative-store.
      const actions = document.createElement('div');
      actions.className = 'bb2-unavailable__actions';
      actions.append(back, change);
      variantsEl.replaceChildren(text, actions);
    }
    this.#setSecondaryFooterVisible(false);
  }

  /**
   * The v4 availability call ERRORED (network / non-2xx / parse) — scenario 2.1. We do NOT
   * fail open (the shopper must not add on unknown availability), so replace the body with
   * a "something went wrong" message + a "Try again" button that refetches this product's
   * v4 and re-resolves the picker. Reuses the unavailable-state styles (EA-3873 PR-2).
   * @param {object} ctx
   */
  #renderSecondaryError(ctx) {
    this.#secondaryLoading = false;
    const variantsEl = this.querySelector('[data-bb2-variants]');
    const nameEl = this.querySelector('[data-bb2-product-name]');
    const gw = this.querySelector('[data-bb2-giftwrap]');
    if (nameEl) nameEl.style.display = 'none';
    if (gw) gw.hidden = true;
    if (variantsEl) {
      variantsEl.classList.add('bb2-variants--unavailable');
      const text = document.createElement('p');
      text.className = 'bb2-unavailable__text';
      text.textContent = this.dataset.availabilityErrorText || 'Something went wrong. Please try again.';
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'bb2-unavailable__change';
      retry.setAttribute('data-bb2-retry', '');
      retry.textContent = this.dataset.availabilityRetryText || 'Try again';
      retry.addEventListener('click', () => {
        // Still on the same product → re-resolve (loader → variants / 2.2 / 2.1). The failed
        // promise was already cleared, so #ensureCardCaps starts a fresh call.
        if (this.#secondaryCtx === ctx) this.#applyVariantAvailability(ctx);
      });
      variantsEl.replaceChildren(text, retry);
    }
    this.#setSecondaryFooterVisible(false);
  }

  /**
   * Render the availability result: the centered unavailable message when no
   * variant is fulfillable, else the variant tiles + enabled CTA.
   * @param {object} ctx
   * @param {object} ctxInfo
   */
  #renderSecondaryResult(ctx, ctxInfo) {
    const anyAvailable = ctx.variants.some((v) => v.fulfillmentAvailable !== false);
    if (!anyAvailable) {
      this.#renderSecondaryUnavailable(ctxInfo.fulfillmentType);
      // No variant is fulfillable → the product can't be added. Disable its Add
      // CTA (drawer + BB page) and drop it from the draft.
      this.#markProductUnavailable(ctx.productId);
      return;
    }
    this.#renderVariantsInto(ctx);
    this.#updateSecondaryAddState();
  }

  /** Store selection changed → availability may differ; re-enable everything. */
  #handleStoreAvailabilityChange = () => {
    // Nothing is built yet; #hydrate replays this (see #missedStoreChange).
    if (!this.#hydrated) {
      this.#missedStoreChange = true;
      return;
    }
    // Abort any background prefetch for the OLD store at once — #warmAvailability below
    // restarts it for the new context once the priority-1 reconcile resolves.
    this.#prefetchGen += 1;
    this.#prefetchQueue = [];
    this.#clearUnavailableProducts();
    // A Same Day / 1-Hour window drops the "Free delivery" milestone from the
    // discount progress bar (and switching back to Next Day / later restores it),
    // so re-render the markers against the new selection.
    this.#renderProgressMarkers();
    this.#updateProgress();
    // The ATC CTA is gated on having a store selected — refresh it so selecting
    // (or removing) a store immediately enables/disables checkout.
    this.#updateFooter();
    // Keep the footer store/delivery pills in step with the new selection.
    this.#reflectStoreSelection();
    // An open picker is still showing the sizes it resolved for the OLD selection —
    // or none at all: opened with no store, it rendered every size unchecked beneath
    // the selector. Re-ask for the new one, or a size the saved store cannot make
    // goes into the bundle and is only refused in the cart (EA-3985). Keyed so the
    // save's paired events do not ask twice.
    const openCtx = this.#secondaryCtx;
    if (openCtx && this.#secondaryResolvedKey !== (this.#availabilityContext()?.key ?? null)) {
      this.#applyVariantAvailability(openCtx);
    }
    // The card grid re-filters against the new store, but the in-progress draft
    // holds items chosen at the OLD store — some may not be carried by the new
    // one. Reconcile the draft (priority-1) so a stale (partly-unavailable) selection
    // can't be carried through to the cart, THEN warm the rest in the background.
    this.#warmAvailability();
  };

  /**
   * Re-validate every bundle item (draft AND committed) against the newly-selected
   * store, and drop any the new store can't fulfil — at TWO levels:
   *   1. Arrangement level — the store doesn't carry the product at all (the SAME
   *      decision that shows/hides the product cards; shared, cached fetch).
   *   2. Per-variant level (EA-3873 2b) — the store carries the product but not this
   *      specific SIZE, via the same v4 availability call the secondary picker uses.
   * Committed items (draft:false) also have their CART lines removed (EA-3873 2c) —
   * silently, since the cart syncs into this drawer, the BB page and the cart
   * "Add more" rail, so a stale line left behind would re-appear.
   *
   * Fails OPEN at every step: no store / shipment / fetch failure / no matching card
   * / no v4 answer → the item is kept, mirroring how the card filter shows everything
   * in those cases.
   */
  async #reconcileDraftWithStore() {
    // Bump the generation FIRST — before any early return — so that a later
    // invocation which bails early (store removed, shipment, or empty draft)
    // still invalidates an in-flight fetch from a prior valid store. Otherwise
    // that stale fetch would pass the gen check below and mutate the draft.
    const gen = ++this.#storeReconcileGen;

    if (!this.#selectedItems.length) return;

    const store = this.#getStoreState();
    // No usable selection → nothing to reconcile. Shipment IS reconciled now (v4's shipment[]
    // answers shippability per product); #availabilityContext returns null when it's unusable.
    if (!store) return;

    // EA-3873: v4 is the single source of truth (the arrangements level was dropped — its data
    // isn't persistent per BE). Fetch per-variant availability for EVERY bundle/cart item in ONE
    // combined call (priority-1) and drop any variant the new store explicitly can't fulfil.
    const ctxInfo = this.#availabilityContext();
    if (!ctxInfo) return; // no usable context → fail open (keep the draft)
    const extIds = [];
    for (const item of this.#selectedItems) {
      const ext = this.#variantExternalId(item);
      if (ext != null) extIds.push(ext);
    }
    if (!extIds.length) return;
    let caps;
    try {
      caps = await this.#fetchAvailabilityBatch(ctxInfo, extIds);
    } catch {
      return; // fetch failed → fail open (keep the draft)
    }
    // A newer store change superseded us while the v4 call was in flight.
    if (gen !== this.#storeReconcileGen) return;

    const removed = [];
    for (const item of this.#selectedItems) {
      const ext = this.#variantExternalId(item);
      const c = ext != null ? caps.get(ext) : null;
      // Drop ONLY on an explicit v4 `false` for the chosen method — a missing entry stays
      // (fail open), so a product v4 doesn't answer for is never silently removed.
      if (c && c[ctxInfo.fulfillmentType] === false) removed.push(item);
    }
    if (!removed.length) return;

    // Committed items (draft:false) are in the CART — collect their variant ids so
    // their lines (+ any gift-wrap children) are removed from the cart as well.
    const committedVariantIds = removed.filter((i) => i.draft === false).map((i) => Number(i.variantId));

    for (const item of removed) {
      this.#removeByProduct(item.productId);
      this.#applyCardState(item.productId);
    }
    this.#reorderCardsByBundle();
    this.#updateProgress();
    this.#updateFooter();
    this.#saveState();

    if (committedVariantIds.length) await this.#removeBundleLinesFromCart(committedVariantIds);
  }

  /**
   * Remove committed bundle cart lines for the given variant ids — plus any gift-wrap /
   * add-on children sharing their `_add_to_cart_id` — in one /cart/update.js call, then
   * broadcast a CartUpdateEvent so the header bubble + open cart drawer refresh. Used by
   * the store-change reconcile to silently drop items the newly-selected store can't fulfil.
   * @param {number[]} variantIds
   */
  async #removeBundleLinesFromCart(variantIds) {
    const wanted = new Set(variantIds.map(Number));
    const cart = await this.#fetchCart();
    if (!cart || !Array.isArray(cart.items)) return;

    const updates = {};
    const groupIds = new Set();
    // Zero every bundle line whose variant is being dropped; note its group id (if any)
    // so grouped gift-wrap/add-on children go with it.
    for (const item of cart.items) {
      const props = item.properties || {};
      const isBundle = String(props[CART_PROPERTY_KEYS.itemType]) === CART_ITEM_TYPES.bundleLineItem;
      if (isBundle && wanted.has(Number(item.variant_id))) {
        updates[item.key] = 0;
        const gid = props[CART_PROPERTY_KEYS.addToCartId];
        if (gid) groupIds.add(String(gid));
      }
    }
    if (!Object.keys(updates).length) return;

    if (groupIds.size) {
      for (const item of cart.items) {
        const gid = (item.properties || {})[CART_PROPERTY_KEYS.addToCartId];
        if (gid && groupIds.has(String(gid))) updates[item.key] = 0;
      }
    }

    try {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      });
      if (!res.ok) return;
      const updated = await res.json();
      document.dispatchEvent(
        new CartUpdateEvent(updated, this.id || 'ea-bundle-builder-drawer', { itemCount: updated.item_count }),
      );
      // The removal may have emptied the bundle — reconcile the BYOB cart flag so it
      // never outlives the bundle lines it describes (best-effort; invisible metadata).
      const stillHasBundle =
        Array.isArray(updated.items) &&
        updated.items.some(
          (it) => String((it.properties || {})[CART_PROPERTY_KEYS.itemType]) === CART_ITEM_TYPES.bundleLineItem,
        );
      await this.#syncByobAttribute(updated.attributes, stillHasBundle).catch(() => {});
    } catch {
      // Best-effort — a failed removal leaves the line; the checkout availability
      // check remains the backstop.
    }
  }

  /**
   * Keep the cart-level `_is_byob_order` attribute in step with whether the cart holds any
   * bundle lines: `true` while bundle lines remain, cleared ('') once none do. Read by downstream
   * fulfillment from cart.attributes. No-ops when the attribute already matches (and no legacy
   * `byob` lingers), so the common paths issue no extra request.
   *
   * DO NOT REMOVE (EA-4411): the `_is_byob_order` cart attribute is consumed by the Segment custom
   * pixel (docs/segment-checkout-pixel.js) to set the `byob` flag on the Order Completed /
   * order-place event. Checkout is outside theme control, so the pixel can only read cart
   * attributes — if this attribute stops being written, BYOB orders will silently report
   * byob:false in Segment.
   * @param {object|undefined} currentAttrs the cart's current `attributes` object
   * @param {boolean} hasBundle whether the cart will hold bundle lines after the operation
   * @param {AbortSignal} [signal]
   */
  async #syncByobAttribute(currentAttrs, hasBundle, signal) {
    const attrs = currentAttrs || {};
    const current = attrs._is_byob_order;
    const isSet = current === true || current === 'true';
    const legacyByobSet = attrs.byob === true || attrs.byob === 'true';
    /** @type {Record<string, unknown>} */
    const update = {};
    if (isSet !== hasBundle) update._is_byob_order = hasBundle ? true : '';
    // Clear any legacy `byob` attribute left on carts from before the rename.
    if (legacyByobSet) update.byob = '';
    if (!Object.keys(update).length) return;
    // Caller-provided signal owns the lifetime (e.g. the ATC batch's 30s controller). When none
    // is supplied (the no-change path and #removeBundleLinesFromCart call this unawaited), add an
    // internal timeout so a stalled request can't leave the promise pending forever.
    let timeoutId;
    let activeSignal = signal;
    if (!activeSignal) {
      const controller = new AbortController();
      activeSignal = controller.signal;
      timeoutId = setTimeout(() => controller.abort(), 30000);
    }
    try {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: update }),
        signal: activeSignal,
      });
      if (!res.ok) throw new Error(`Cart attribute update failed: ${res.status}`);
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }
  }

  /**
   * Mark a product as not fulfillable for the current store/delivery: disable its
   * Add CTA (drawer card + BB-page card via event) and drop any draft line for it,
   * so it can't be added to the bundle. Cleared on the next store change.
   * @param {number|string} productId
   */
  #markProductUnavailable(productId) {
    const id = Number(productId);
    this.#unavailableProducts.add(id);

    const btn = this.querySelector(`.bb-card[data-product-id="${id}"] .bb-card__action`);
    if (btn) {
      btn.disabled = true;
      btn.classList.add('bb-card__action--unavailable');
    }

    // BB-page product cards (bb-product-card-cta.js) disable their own CTA.
    document.dispatchEvent(new CustomEvent('ea:bb-product-unavailable', { detail: { productId: id } }));

    // Ensure it isn't left in the draft bundle.
    if (this.#selectedItems.some((i) => i.productId === id)) {
      this.#removeByProduct(id);
      this.#applyCardState(id);
      this.#updateProgress();
      this.#updateFooter();
      this.#saveState();
    }
  }

  /** Re-enable all products previously marked unavailable (store selection changed). */
  #clearUnavailableProducts() {
    if (!this.#unavailableProducts.size) return;
    this.#unavailableProducts.clear();
    for (const btn of this.querySelectorAll('.bb-card__action--unavailable')) {
      btn.disabled = false;
      btn.classList.remove('bb-card__action--unavailable');
    }
    // Let BB-page cards re-enable their CTAs too.
    document.dispatchEvent(new CustomEvent('ea:bb-availability-reset'));
  }

  /** Confirm the secondary selection and add the product to the bundle. */
  handleSecondaryAdd(event) {
    event.preventDefault();
    const ctx = this.#secondaryCtx;
    if (!ctx || !ctx.selectedVariantId) return;

    const variant = ctx.variants.find((v) => v.id === ctx.selectedVariantId) || ctx.variants[0];
    this.#commitBundleItem(ctx, variant);
    this.#closeSecondary();
  }

  /**
   * Build the secondary variant tile markup.
   * @param {object[]} variants
   * @param {string} productTitle
   * @param {number|null} selectedId
   * @returns {string}
   */
  #renderSecondaryVariants(variants, productTitle, selectedId) {
    // Hide variants the availability API reported as not fulfillable for the
    // selected store/delivery (fulfillmentAvailable === false). Variants whose
    // availability is unknown (undefined — no store / fetch pending / failed)
    // stay visible so we never blank the picker on a transient error.
    const visible = variants.filter((v) => v.fulfillmentAvailable !== false);
    if (visible.length === 0) {
      return '<p class="bb2-variants__empty">This item isn&rsquo;t available for your selected store and delivery option.</p>';
    }
    const single = visible.length === 1;
    return visible
      .map((v) => {
        const isSelected = v.id === selectedId;
        const showTitle = v.title && v.title !== 'Default Title' ? v.title : productTitle;
        const priceText = this.#formatMoney(v.price);
        const compareHTML = v.compare > v.price ? `<span class="bb2-variant__compare">${this.#formatMoney(v.compare)}</span>` : '';
        const imageHTML = v.image
          ? `<img class="bb2-variant__image" src="${this.#escapeAttr(v.image)}" alt="${this.#escapeAttr(showTitle)}" width="64" height="64" loading="lazy">`
          : '<span class="bb2-variant__image bb2-variant__image--placeholder"></span>';
        return `
          <button type="button" class="bb2-variant${isSelected ? ' bb2-variant--selected' : ''}${single ? ' bb2-variant--single' : ''}"
            data-variant-id="${v.id}"
            data-price="${v.price}"
            data-compare="${v.compare}"
            ${v.available ? '' : 'disabled'}>
            ${imageHTML}
            <span class="bb2-variant__info">
              <span class="bb2-variant__title">${this.#escapeHTML(showTitle)}</span>
              <span class="bb2-variant__pricing"><span class="bb2-variant__price">${priceText}</span>${compareHTML}</span>
            </span>
          </button>
        `;
      })
      .join('');
  }

  // ── Variant availability (secondary drawer) ─────────────────────

  /**
   * Read the store selection the store selector persisted. Prefers the live
   * getter exposed by store-availability.js, falling back to raw localStorage.
   * @returns {object|null}
   */
  #getStoreState() {
    try {
      if (typeof window.getStoreAvailability === 'function') {
        return window.getStoreAvailability();
      }
      const raw = localStorage.getItem(STORE_STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Paint the footer store/delivery pills from the persisted selection, mirroring
   * store-availability.js's #updateAllTriggers. Needed because the footer trigger
   * ships inside the drawer's <template> and only enters the DOM at #hydrate —
   * after store-availability already fanned the restored selection out to every
   * live trigger on the page. Without this the pre-selected store shows blank on
   * first open. Subsequent store changes reach the (now live) footer through
   * store-availability's own broadcast; this keeps it correct regardless.
   */
  #reflectStoreSelection() {
    // Hide the bundle progress bar until a store/fulfillment is selected — its discount
    // thresholds are meaningless before the shopper can build/buy. Keyed on the
    // authoritative #hasStoreSelected (shipment counts as selected); CSS hides
    // .bundle-progress under .bb-drawer--no-store.
    const drawer = this.refs.drawer;
    if (drawer) drawer.classList.toggle('bb-drawer--no-store', !this.#hasStoreSelected());

    const trigger = this.querySelector('[data-store-availability-trigger]');
    if (!trigger) return;
    const storeText = trigger.querySelector('[data-store-text]');
    const deliveryText = trigger.querySelector('[data-delivery-text]');
    const state = this.#getStoreState();

    if (!state) {
      trigger.removeAttribute('data-store-selected');
      if (storeText) storeText.textContent = '';
      if (deliveryText) deliveryText.textContent = '';
      return;
    }

    trigger.setAttribute('data-store-selected', '');
    if (storeText) storeText.textContent = state.zipCode || state.storeName || state.storeAddress || '';
    if (deliveryText) deliveryText.textContent = triggerDeliveryLabel(state);
  }

  /**
   * Show/hide the footer's clear (X) store icon based on whether the cart holds
   * any item. A non-empty cart is committed to the selected store, so clearing it
   * from the drawer is disallowed — the icon is hidden via a CSS attribute hook.
   * @param {number} itemCount - the cart's total item count.
   */
  #reflectStoreClearAvailability(itemCount) {
    const trigger = this.querySelector('[data-store-availability-trigger]');
    if (!trigger) return;
    if (itemCount > 0) trigger.setAttribute('data-cart-has-items', '');
    else trigger.removeAttribute('data-cart-has-items');
  }

  /**
   * True when the persisted selection is national shipping (`fulfillmentType:
   * 'shipment'`). Every bundle-eligible variant is sellable for shipment, so the
   * per-variant availability API is skipped and all variants render.
   * @returns {boolean}
   */
  #isShipment() {
    const s = this.#getStoreState();
    return !!(s && s.fulfillmentType === 'shipment');
  }

  /**
   * True when the shopper has an active store/fulfillment selection. A store is
   * required to proceed to cart from the drawer: national shipment carries no
   * storeId but is still a valid selection, so treat it as selected too.
   * @returns {boolean}
   */
  #hasStoreSelected() {
    const s = this.#getStoreState();
    return !!(s && (s.storeId || s.fulfillmentType === 'shipment'));
  }

  /**
   * Numeric variant id for the availability API: the external_variant_id
   * metafield with its country prefix stripped (e.g. "US-11729" → 11729).
   * @param {object} v - variant object with an `externalVariantId` field
   * @returns {number|null}
   */
  #variantExternalId(v) {
    const raw = v && v.externalVariantId != null ? String(v.externalVariantId).trim() : '';
    if (!raw) return null;
    // A comma-separated multi-market value ("US-17903,CA-19619") must resolve to the token for
    // the SELECTED country, not whichever one parseInt reaches first. canonicalEaId maps a
    // translated prefix ("É.-U.-11729", EA-4392) back to its country first, so the match below
    // still works. Falls back to an unprefixed token, then the first token's digit tail — and a
    // plain single id ("US-11729", "11729") resolves exactly as before.
    const country = String(this.#getStoreState()?.country || 'US').toUpperCase();
    const tokens = raw
      .split(',')
      .map((t) => canonicalEaId(t.trim()))
      .filter(Boolean);
    if (!tokens.length) return null;

    const prefixRe = /^([A-Za-z]{2})-(.+)$/;
    let chosen = null;
    // 1. Exact country-prefix match.
    for (const t of tokens) {
      const m = t.match(prefixRe);
      if (m && m[1].toUpperCase() === country) {
        chosen = m[2];
        break;
      }
    }
    // 2. Unprefixed (country-agnostic) token.
    if (chosen == null) {
      for (const t of tokens) {
        if (!prefixRe.test(t)) {
          chosen = eaIdDigits(t);
          break;
        }
      }
    }
    // 3. First token, prefix stripped.
    if (chosen == null) {
      const m = tokens[0].match(prefixRe);
      chosen = m ? m[2] : eaIdDigits(tokens[0]);
    }

    const n = parseInt(chosen, 10);
    return Number.isFinite(n) ? n : null;
  }

  /** Today as local YYYY-MM-DD — used when the selection has no delivery date. */
  #todayIso() {
    const d = new Date();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${m}-${day}`;
  }

  /**
   * Resolve the availability context (store + date + fulfillment) into the
   * fields the API needs and a cache key. Returns null when no store is chosen.
   * @param {object|null} [store]
   * @returns {{key:string, fulfillmentType:string, date:string, country:string, zipCode:string, storeId:number}|null}
   */
  #availabilityContext(store = this.#getStoreState()) {
    if (!store) return null;
    // Shipment is nationwide — it carries NO local storeId, but it still has a zip + date, and
    // v4 answers shippability per product from its `shipment[]` array. So build a context for it
    // (storeId omitted from the payload below) instead of bailing — otherwise no v4 ever fires
    // on a shipment-only zip (EA-3873). Every OTHER fulfilment type still requires a storeId.
    if (store.fulfillmentType !== 'shipment' && !store.storeId) return null;
    // Shipment is its OWN type, not a flavour of delivery. It used to collapse to
    // 'delivery' harmlessly, because shipment carried no storeId and this method
    // returned null before the distinction could matter. Now that a shipment store
    // is persisted, collapsing it would gate shipment variants on LOCAL DELIVERY
    // caps — hiding variants that ship fine, and #applyCaps would then move the
    // shopper off their selected variant.
    const fulfillmentType = store.fulfillmentType === 'pickup' ? 'pickup' : store.fulfillmentType === 'shipment' ? 'shipment' : 'delivery';
    const date = store.deliveryDate || this.#todayIso();
    // Null for shipment (no local store) — omitted from the v4 payload; otherwise a number.
    const storeId = store.storeId != null && store.storeId !== '' ? Number(store.storeId) : null;
    const country = String(store.country || 'US').toUpperCase();
    // The persisted store selection stores the zip under `zipCode` (not `zip`) —
    // reading `store.zip` sent an empty zipCode and the proxy 400'd, which silently
    // fell through to "show all variants" (availability gating disabled).
    const zipCode = store.zipCode || store.zip || '';
    return {
      // Key covers EVERY request input (store, date, fulfillment, country, zip) so
      // cached caps can't be reused across a changed zip/country/date/fulfillment.
      // Shipment has no storeId → key on 'ship' so its caps don't collide with a store's.
      key: `${storeId ?? 'ship'}:${date}:${fulfillmentType}:${country}:${zipCode}`,
      fulfillmentType,
      date,
      country,
      zipCode,
      storeId,
    };
  }

  /**
   * POST a batch of variant ids to the availability proxy and merge the returned
   * capabilities into the context's caps map (only requesting ids we don't
   * already hold). Resolves to that caps map. Rejects on network / HTTP error.
   * @param {object} ctxInfo - from #availabilityContext
   * @param {number[]} productIds
   * @returns {Promise<Map<number, object>>}
   */
  async #fetchAvailabilityBatch(ctxInfo, productIds) {
    let caps = this.#capsByContext.get(ctxInfo.key);
    if (!caps) {
      caps = new Map();
      this.#capsByContext.set(ctxInfo.key, caps);
    }
    const missing = productIds.filter((id) => !caps.has(id));
    if (missing.length === 0) return caps;

    // Abort if the request hangs so callers settle and the Add spinner never sticks
    // (the picker surfaces a rejection as the 2.1 "Try again" state; the background
    // prefetch swallows it until the shopper opens that card).
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);
    let data;
    try {
      const payload = {
        productIds: missing,
        date: ctxInfo.date,
        country: ctxInfo.country,
        zipCode: ctxInfo.zipCode,
        // orderType 1 = delivery, 2 = pickup, 3 = shipment. Mirrors the arrangements call.
        orderType: orderTypeFor(ctxInfo.fulfillmentType),
      };
      // Shipment carries no storeId — omit it (mirrors the PLP arrangements-for-shipment
      // call). Sending null/0 would make the proxy scope to a non-existent store.
      if (ctxInfo.storeId != null) payload.storeId = ctxInfo.storeId;
      const response = await fetch(AVAILABILITY_API.product, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`availability ${response.status}`);
      data = await response.json();
    } finally {
      clearTimeout(timeoutId);
    }

    // Availability is read from the per-fulfillment-type arrays (delivery[] /
    // pickup[] / shipment[]), keyed by item.productId (= the external id we sent). A
    // variant is available for a type when EITHER `available` OR
    // `arrangementsAvailable` is true. (NOT products[].fulfillmentCapabilities —
    // that can disagree with the real per-date availability.) #applyCaps then
    // reads caps[ctxInfo.fulfillmentType] for the shopper's chosen method.
    const mergeList = (list, type) => {
      if (!Array.isArray(list)) return;
      for (const entry of list) {
        if (!entry || entry.productId == null) continue;
        const id = Number(entry.productId);
        const capsForId = caps.get(id) || {};
        // Shipment availability is the per-date `available` flag ONLY. Its `arrangementsAvailable`
        // is a catalog signal that can be true even when the item can't ship on the chosen date
        // (EA-3873) — OR-ing it in wrongly marked shipment-unavailable products (available:false,
        // arrangementsAvailable:true) as available. Delivery/pickup keep the either-flag rule.
        capsForId[type] =
          type === 'shipment'
            ? entry.available === true
            : entry.available === true || entry.arrangementsAvailable === true;
        caps.set(id, capsForId);
      }
    };
    mergeList(data && data.delivery, 'delivery');
    mergeList(data && data.pickup, 'pickup');
    // `shipment[]` entries carry the same `available` flag, answering it for the
    // date we sent — which is the only date this context describes. Their `dates[]`
    // alternatives are deliberately ignored: they'd say a variant is shippable on
    // SOME other date, not the one the shopper chose.
    mergeList(data && data.shipment, 'shipment');
    // Remember which stores can ship each available variant (EA-4071, see
    // #shipmentStoresByExtId) — the only place the drawer sees `stores[]`.
    for (const entry of data && Array.isArray(data.shipment) ? data.shipment : []) {
      if (!entry || entry.productId == null || entry.available !== true) continue;
      const storeIds = (entry.stores || []).map((st) => st && st.storeId).filter((id) => id != null).map(String);
      if (storeIds.length) this.#shipmentStoresByExtId.set(Number(entry.productId), storeIds);
    }
    return caps;
  }

  /**
   * EA-4071: before Add All to Cart on a shipment selection that names no store, write the
   * store v4 says ships the bundle into the selection (storage → cart attributes → header
   * triggers → change event, the same sequence as the personalization drawer's
   * #adoptShipmentStore). Without it the add-to-cart interceptor has no store to stamp,
   * refuses the add and reopens the store selector — which saves the same store-less
   * selection, so the shopper loops.
   *
   * Prefers a store that ships EVERY bundle item (the cart carries one store_id), else the
   * first store any item names. Fetches v4 for items whose stores aren't known yet. No store
   * named → leaves the selection alone, and the interceptor's refusal stands.
   */
  async #adoptShipmentStoreForBundle() {
    const sel = readStoreSelection();
    if (!sel || sel.fulfillmentType !== 'shipment' || sel.storeId) return;
    const extIds = this.#selectedItems.map((item) => this.#variantExternalId(item)).filter((id) => id != null);
    if (!extIds.length) return;

    const unknown = extIds.filter((id) => !this.#shipmentStoresByExtId.has(id));
    const ctxInfo = unknown.length ? this.#availabilityContext() : null;
    if (ctxInfo) {
      // Caps already cached for an id skip the request, so drop them from this context's
      // map first — the fetch has to run to read their `stores[]`.
      const caps = this.#capsByContext.get(ctxInfo.key);
      if (caps) unknown.forEach((id) => caps.delete(id));
      try {
        await this.#fetchAvailabilityBatch(ctxInfo, unknown);
      } catch {
        // Nothing to adopt from — the interceptor's refusal is the answer.
      }
    }

    const lists = extIds.map((id) => this.#shipmentStoresByExtId.get(id) || []);
    const first = lists.find((list) => list.length);
    if (!first) return;
    const storeId = first.find((id) => lists.every((list) => !list.length || list.includes(id))) || first[0];

    // `storeNumber` is blanked, not carried: `stores[]` here is a list of IDS
    // (#collectShipmentStores keeps nothing else), so the adopted store's number is
    // unknown — and the number beside a DIFFERENT store's id would be worse than none.
    // `readStoreNumber` falls back to the id, exactly as it did before this field existed.
    const state = buildStoreState({ ...sel, storeId, storeNumber: '' });
    if (!saveStoreContext(state)) return; // storage blocked — don't desync the UI
    syncCartAttributes(state);
    updateStoreTriggers(state);
    emitStoreChange(state);
  }

  // ── Background v4 prefetch (EA-3873 PR-2) ────────────────────────────────────
  // Once a store is selected, warm #capsByContext for the primary cards in the
  // background (a few concurrent, one call per card) so opening a card's picker is
  // instant. The picker's existing "fast path" (all this card's caps cached → render,
  // no loader) fires automatically once warm. Secondary cards are excluded — they are
  // gated by the single batched call in the card filters (fetchSecondaryVariantCaps).

  /**
   * Resolve a card's v4 caps, SHARING an in-flight call (background prefetch or a prior
   * Add) rather than firing a duplicate. Resolves immediately if already cached; rejects
   * if the underlying fetch errors (callers surface the 2.1 error state — never fail open).
   * @param {number} productId
   * @param {number[]} extIds  the card's bundle-eligible external variant ids
   * @param {object} ctxInfo   the current availability context
   * @returns {Promise<Map<number, object>>}
   */
  #ensureCardCaps(productId, extIds, ctxInfo) {
    const cached = this.#capsByContext.get(ctxInfo.key);
    if (cached && extIds.every((id) => cached.has(id))) return Promise.resolve(cached);
    const key = `${ctxInfo.key}:${productId}`;
    const existing = this.#cardFetchPromises.get(key);
    if (existing) return existing;
    const p = this.#fetchAvailabilityBatch(ctxInfo, extIds).finally(() => {
      if (this.#cardFetchPromises.get(key) === p) this.#cardFetchPromises.delete(key);
    });
    this.#cardFetchPromises.set(key, p);
    return p;
  }

  /** The bundle-eligible external variant ids for a card, from its `data-variants` payload. */
  #cardExtIds(card) {
    const btn = card.querySelector('.bb-card__action');
    if (!btn) return [];
    const ids = new Set();
    const variants = this.#parseJSON(btn.dataset.variants, []);
    if (Array.isArray(variants) && variants.length) {
      for (const v of variants) {
        const ext = this.#variantExternalId(v);
        if (ext != null) ids.add(ext);
      }
    } else {
      const ext = this.#variantExternalId({ externalVariantId: btn.dataset.externalVariantId });
      if (ext != null) ids.add(ext);
    }
    return [...ids];
  }

  /** Drop a card from the pending prefetch queue (it's being fetched on demand now). */
  #dequeuePrefetch(productId) {
    const pid = Number(productId);
    this.#prefetchQueue = this.#prefetchQueue.filter((job) => job.productId !== pid);
  }

  /**
   * Rebuild the background prefetch queue for the current store and start pumping it.
   * Bumps the generation so any prior run bails. Primary cards only; skips cards already
   * cached for this context. Called after the priority-1 reconcile (bundle/cart) resolves.
   */
  #startPrefetch() {
    const gen = ++this.#prefetchGen;
    this.#prefetchQueue = [];
    const ctxInfo = this.#availabilityContext();
    // No resolved selection (no store, or shipment with no zip) → nothing to warm. Shipment
    // itself IS warmed now — v4's shipment[] answers shippability per product (EA-3873).
    if (!ctxInfo) {
      this.#prefetchCtx = null;
      return;
    }
    this.#prefetchCtx = ctxInfo;
    const cached = this.#capsByContext.get(ctxInfo.key);
    const seen = new Set();
    for (const card of this.querySelectorAll('.bb-card[data-product-id]')) {
      if (card.getAttribute('data-bb-secondary') === 'true') continue; // primary only
      const pid = Number(card.dataset.productId);
      if (!pid || seen.has(pid)) continue;
      seen.add(pid);
      const extIds = this.#cardExtIds(card);
      if (!extIds.length) continue;
      if (cached && extIds.every((id) => cached.has(id))) continue; // already warm
      this.#prefetchQueue.push({ productId: pid, extIds });
    }
    this.#pumpPrefetch(gen, ctxInfo);
  }

  /**
   * Pump the prefetch queue up to PREFETCH_CONCURRENCY in flight. Each job shares the
   * per-card dedup (#ensureCardCaps), so an Add that lands mid-prefetch awaits the same
   * call. A background failure is swallowed here — it's surfaced (2.1) only if the shopper
   * actually opens that card. Bails when a newer store change bumps the generation.
   * @param {number} gen
   * @param {object} ctxInfo
   */
  #pumpPrefetch(gen, ctxInfo) {
    if (!ctxInfo) return;
    while (this.#prefetchActive < PREFETCH_CONCURRENCY && this.#prefetchQueue.length) {
      if (gen !== this.#prefetchGen) return;
      const job = this.#prefetchQueue.shift();
      this.#prefetchActive += 1;
      this.#ensureCardCaps(job.productId, job.extIds, ctxInfo)
        .catch(() => {})
        .finally(() => {
          this.#prefetchActive -= 1;
          // Resume the CURRENT queue with the CURRENT generation + its stored context — so a job
          // settling from an OLDER generation still frees its slot for and drains the current
          // queue, instead of bailing on the gen mismatch and stalling it.
          this.#pumpPrefetch(this.#prefetchGen, this.#prefetchCtx);
        });
    }
  }

  /**
   * Warm availability after a store is selected / the page loads: PRIORITY-1 first — the
   * bundle/cart variants, via the existing reconcile (one combined v4 call that also
   * removes anything the new store can't fulfil) — THEN the background prefetch for the
   * rest of the primary cards.
   */
  #warmAvailability() {
    // Block checkout + shimmer the carousels while priority-1 (v4 for the bundle/cart) resolves,
    // then release and warm the rest of the PRIMARY cards one-by-one in the background.
    this.#setPriorityLoading(true);
    // In PARALLEL: one batched v4 call for EVERY secondary (BYOB add-on) product, so their
    // add-gates resolve instantly (EA-3873). Fire-and-forget; independent of priority-1.
    this.#warmSecondary();
    Promise.resolve(this.#reconcileDraftWithStore()).finally(() => {
      this.#setPriorityLoading(false);
      this.#startPrefetch();
    });
  }

  /**
   * One batched v4 call for every SECONDARY (BYOB add-on) product's bundle-eligible variants —
   * fired in parallel with the primary warm so the secondary add-gates are prewarmed. Reads the
   * pre-stripped ids from each `.bb-card[data-bb-secondary]`'s `data-bb-variant-ext-ids`.
   * Fire-and-forget: a failure just leaves them to resolve on demand.
   */
  #warmSecondary() {
    const ctxInfo = this.#availabilityContext();
    if (!ctxInfo) return;
    const ids = new Set();
    for (const card of this.querySelectorAll('.bb-card[data-bb-secondary="true"]')) {
      (card.getAttribute('data-bb-variant-ext-ids') || '').split(',').forEach((s) => {
        const n = Number(s.trim());
        if (s.trim() && !Number.isNaN(n)) ids.add(n);
      });
    }
    if (!ids.size) return;
    this.#ensureCardCaps('secondary-batch', [...ids], ctxInfo).catch(() => {});
  }

  /**
   * Toggle the priority-1 loading state: shimmer the drawer's carousels (its own
   * `data-bb-priority-loading` attribute, independent of bb-secondary-availability's
   * `data-bb-refreshing` so the two can't clear each other early) and re-gate the footer
   * ATC (disabled while true).
   * @param {boolean} on
   */
  #setPriorityLoading(on) {
    this.#priorityLoading = on;
    this.toggleAttribute('data-bb-priority-loading', on);
    this.#updateFooter();
  }

  /**
   * Set each variant's `fulfillmentAvailable` flag from a caps map and re-pick a
   * default variant if the current selection is now hidden.
   * @param {object} ctx
   * @param {Map<number, object>} caps
   * @param {object} ctxInfo
   * @param {Map<object, number>} idByVariant
   */
  #applyCaps(ctx, caps, ctxInfo, idByVariant) {
    for (const v of ctx.variants) {
      const extId = idByVariant.get(v);
      if (extId == null) {
        v.fulfillmentAvailable = undefined; // ungateable → stays visible
        continue;
      }
      const c = caps.get(extId);
      v.fulfillmentAvailable = !!(c && c[ctxInfo.fulfillmentType] === true);
    }
    const stillSelectable = ctx.variants.some((v) => v.id === ctx.selectedVariantId && v.fulfillmentAvailable !== false);
    if (!stillSelectable) {
      const fallback =
        ctx.variants.find((v) => v.fulfillmentAvailable !== false && v.available) ||
        ctx.variants.find((v) => v.fulfillmentAvailable !== false) ||
        null;
      ctx.selectedVariantId = fallback ? fallback.id : null;
    }
  }

  /** Render the secondary variant tiles from ctx and (re)bind click handlers. */
  #renderVariantsInto(ctx) {
    const variantsEl = this.querySelector('[data-bb2-variants]');
    if (!variantsEl) return;
    // Reset any prior loading / unavailable state.
    this.#secondaryLoading = false;
    variantsEl.classList.remove('bb2-variants--unavailable');
    const nameEl = this.querySelector('[data-bb2-product-name]');
    if (nameEl) nameEl.style.display = '';
    // #renderSecondaryUnavailable hides the gift-wrap blurb; an open picker that
    // recovers in place (EA-3985 re-check) must get it back, as #openSecondary sets it.
    const gw = this.querySelector('[data-bb2-giftwrap]');
    if (gw) gw.hidden = !ctx.wrapAvailable;
    this.#setSecondaryFooterVisible(true);
    variantsEl.innerHTML = this.#renderSecondaryVariants(ctx.variants, ctx.title, ctx.selectedVariantId);
    for (const tile of variantsEl.querySelectorAll('.bb2-variant:not(.bb2-variant--single)')) {
      tile.addEventListener('click', this.#handleSecondaryVariantClick);
    }
  }

  /** Show shimmer placeholder tiles in the variant list while availability resolves. */
  #renderVariantsLoading() {
    this.#secondaryLoading = true;
    this.#setSecondaryFooterVisible(true);
    const nameEl = this.querySelector('[data-bb2-product-name]');
    if (nameEl) nameEl.style.display = '';
    const variantsEl = this.querySelector('[data-bb2-variants]');
    if (variantsEl) variantsEl.classList.remove('bb2-variants--unavailable');
    if (variantsEl) {
      let tiles = '';
      for (let i = 0; i < 3; i++) {
        tiles +=
          '<div class="bb2-variant bb2-variant--shimmer" aria-hidden="true">' +
          '<span class="bb2-variant__image bb2-shimmer"></span>' +
          '<span class="bb2-variant__info">' +
          '<span class="bb2-shimmer bb2-shimmer--line bb2-shimmer--title"></span>' +
          '<span class="bb2-shimmer bb2-shimmer--line bb2-shimmer--price"></span>' +
          '<span class="bb2-shimmer bb2-shimmer--line bb2-shimmer--badge"></span>' +
          '</span>' +
          '</div>';
      }
      variantsEl.innerHTML =
        '<div class="bb2-variants__loading" role="status" aria-live="polite" aria-label="Checking availability">' + tiles + '</div>';
    }
    const addBtn = this.querySelector('[data-bb2-add]');
    if (addBtn) addBtn.disabled = true;
  }

  /**
   * Gate the secondary drawer's variants by real fulfillment availability for
   * the selected store. Fetches THIS product's variant availability on demand
   * (when the picker opens); renders synchronously from cache if the same
   * product/store picker was opened before, otherwise shows a shimmer while the
   * request resolves.
   *
   * Renders all variants (fail open) only when there is nothing to gate against — no
   * store/zip context, a secondary card (batch-gated already), or no external ids. Shipment
   * IS gated by v4 now (its shipment[] answers shippability per product). A genuine v4 ERROR
   * does NOT fail open: it shows the 2.1 "Try again" state, and none-available shows the 2.2
   * change-store state (EA-3873).
   * @param {object} ctx - the secondary drawer context (this.#secondaryCtx)
   */
  async #applyVariantAvailability(ctx) {
    if (!ctx || !Array.isArray(ctx.variants) || ctx.variants.length === 0) return;
    // The selection this call answers for — read by #handleStoreAvailabilityChange.
    this.#secondaryResolvedKey = this.#availabilityContext()?.key ?? null;

    // Secondary (BYOB add-on) and primary variants both flow through the caps gate below.
    // Secondary caps are pre-warmed by #warmSecondary (one batched v4 for every secondary
    // product), so the fast path below hits from cache with no extra call, and the drawer/cart
    // no longer renders an addable size the store can't fulfil — matching what the BB page grid
    // already hides (EA-3873).

    // Map each (bundle-eligible) variant to its external id and send ONLY those
    // in the availability payload — non-eligible variants are never queried.
    const idByVariant = new Map();
    const requestIds = new Set();
    for (const v of ctx.variants) {
      const extId = this.#variantExternalId(v);
      if (extId == null) continue;
      idByVariant.set(v, extId);
      requestIds.add(extId);
    }
    const productIds = [...requestIds];

    const ctxInfo = this.#availabilityContext();

    // No store selected, or nothing gateable → show every variant.
    if (!ctxInfo || productIds.length === 0) {
      this.#renderVariantsInto(ctx);
      return;
    }

    // Fast path: this product's variants were already fetched for this store
    // context (e.g. the picker was opened before) → apply + render, no loader.
    let caps = this.#capsByContext.get(ctxInfo.key);
    if (caps && productIds.every((id) => caps.has(id))) {
      this.#applyCaps(ctx, caps, ctxInfo, idByVariant);
      this.#renderSecondaryResult(ctx, ctxInfo);
      return;
    }

    // Not cached → share any in-flight background prefetch for this card (or start the
    // call now), showing the loader while it resolves. Pull the card from the prefetch
    // queue so the picker fetch and a queued prefetch can't both fire.
    this.#dequeuePrefetch(ctx.productId);
    this.#renderVariantsLoading();
    try {
      caps = await this.#ensureCardCaps(ctx.productId, productIds, ctxInfo);
    } catch (e) {
      // 2.1 — do NOT fail open (the shopper must not add on unknown availability). Show
      // "something went wrong" + Try again, which refetches this product's v4 (EA-3873).
      if (this.#secondaryCtx === ctx) this.#renderSecondaryError(ctx);
      return;
    }

    // Stale — shopper switched products / closed the drawer meanwhile.
    if (this.#secondaryCtx !== ctx) return;
    // Stale — the store/date/fulfillment/zip changed while this request was in
    // flight, so its caps no longer describe the current selection. Ignore it.
    const currentCtx = this.#availabilityContext();
    if (!currentCtx || currentCtx.key !== ctxInfo.key) return;

    this.#applyCaps(ctx, caps, ctxInfo, idByVariant);
    this.#renderSecondaryResult(ctx, ctxInfo);
  }

  // ── Bundle state ─────────────────────────────────────────────────

  /**
   * Add an item to the bundle
   * @param {BundleItem} item
   */
  #addItem(item) {
    if (!this.#selectedItems.some((i) => i.productId === item.productId)) {
      this.#selectedItems.push(item);
    }
  }

  /**
   * Remove all items belonging to a product from the bundle.
   * @param {number} productId
   */
  #removeByProduct(productId) {
    this.#selectedItems = this.#selectedItems.filter((i) => i.productId !== productId);
  }

  // ── Draft persistence (localStorage) ─────────────────────────────
  // The in-progress bundle (selected items + quantities + chosen variant)
  // is mirrored to localStorage so closing/reopening the drawer — or adding a product
  // from the bundle-builder page — restores it. The draft is flushed once the bundle
  // is added to the cart.

  /** Persist the current draft, or clear it when the bundle is empty. */
  #saveState() {
    try {
      if (this.#selectedItems.length === 0) {
        localStorage.removeItem(BB_STORAGE_KEY);
        return;
      }
      localStorage.setItem(BB_STORAGE_KEY, JSON.stringify({ v: BB_DRAFT_VERSION, ts: Date.now(), items: this.#selectedItems }));
    } catch {
      // Storage unavailable (private mode / quota) — persistence is best-effort.
    }
  }

  /** Remove the persisted draft (called after a successful add to cart). */
  #clearState() {
    try {
      localStorage.removeItem(BB_STORAGE_KEY);
    } catch {
      // Ignore.
    }
  }

  /**
   * Rebuild a persisted draft item from the CURRENT card in the drawer, so restored
   * price/title/image/compareAtPrice/variantTitle always reflect live data instead of
   * the stored snapshot. Validates the persisted variant against the card (mirroring
   * #triggerCardAdd's parsing) and returns null if the product/variant is gone or
   * unavailable. Quantity is preserved (clamped).
   * @param {{productId:number, variantId:number, quantity:number}} item
   * @returns {object|null}
   */
  #rebuildRestoredItem(item) {
    const card = this.querySelector(`.bb-card[data-product-id="${item.productId}"]`);
    if (!card) return null;
    const btn = card.querySelector('.bb-card__action');
    if (!btn) return null;

    let price;
    let compareAtPrice;
    let image;
    let variantTitle = '';
    let externalVariantId = '';

    if (btn.dataset.multiVariant === 'true') {
      const variants = this.#parseJSON(btn.dataset.variants, []);
      const match = variants.find((v) => Number(v.id) === item.variantId && v.available);
      if (!match) return null; // variant removed or now unavailable
      price = Number(match.price) || 0;
      compareAtPrice = Number(match.compare) || 0;
      image = match.image || btn.dataset.image || '';
      variantTitle = match.title && match.title !== 'Default Title' ? match.title : '';
      externalVariantId = match.externalVariantId || '';
    } else {
      // Single-variant: drop if sold out or the variant id no longer matches the card.
      // A button held disabled only because the English logic metafields are still loading
      // (data-logic-disabled, non-English pages) is NOT sold out — treat it as available so a
      // restored draft item survives; only a genuinely disabled button (no such flag) drops.
      if (btn.disabled && !btn.hasAttribute('data-logic-disabled')) return null;
      if (Number(btn.dataset.variantId) !== item.variantId) return null;
      price = Number(btn.dataset.price) || 0;
      compareAtPrice = Number(btn.dataset.comparePrice) || 0;
      image = btn.dataset.image || '';
      externalVariantId = btn.dataset.externalVariantId || '';
    }

    let quantity = Number(item.quantity) || 1;
    quantity = Math.min(Math.max(1, quantity), this.#maxQuantity);

    return {
      variantId: item.variantId,
      productId: item.productId,
      title: btn.dataset.title || '',
      image,
      price,
      compareAtPrice,
      handle: btn.dataset.handle || '',
      badge: btn.dataset.badge || '',
      variantTitle,
      externalVariantId,
      quantity,
      isAddon: btn.dataset.addon === 'true',
      seoHidden: card.dataset.seoHidden === 'true',
      // Carry the persisted order through (undefined for cart-synced items with none —
      // #normalizeAddOrders fills those from cart position).
      addOrder: item.addOrder,
    };
  }

  /**
   * Restore a persisted draft into #selectedItems. Drafts past BB_DRAFT_TTL_MS (or with
   * no timestamp) are discarded. Each item is rebuilt from its current card (price/title/
   * image/variant validated live), products no longer offered or with unavailable
   * variants are dropped, quantities clamped to [1, #maxQuantity], duplicates by product
   * removed, and the cleaned set is re-persisted so storage always matches state.
   */
  #restoreState() {
    let raw;
    try {
      raw = localStorage.getItem(BB_STORAGE_KEY);
    } catch {
      return;
    }
    if (!raw) return;

    let data;
    try {
      data = JSON.parse(raw);
    } catch {
      this.#clearState();
      return;
    }

    // Discard stale drafts (missing/expired timestamp) or any written by an older schema
    // version (EA-3873: v1 drafts can't be trusted — see BB_DRAFT_VERSION). This is the
    // one-time cleanup that clears a lingering pre-fix draft left by a cleared cart.
    if (!data || data.v !== BB_DRAFT_VERSION || typeof data.ts !== 'number' || Date.now() - data.ts > BB_DRAFT_TTL_MS) {
      this.#clearState();
      return;
    }

    const items = Array.isArray(data.items) ? data.items : [];
    if (!items.length) return;

    const restored = [];
    const seen = new Set();
    for (const item of items) {
      const productId = Number(item.productId);
      const variantId = Number(item.variantId);
      if (!productId || !variantId || seen.has(productId)) continue;

      const rebuilt = this.#rebuildRestoredItem({
        productId,
        variantId,
        quantity: item.quantity,
        addOrder: item.addOrder,
      });
      if (!rebuilt) continue;

      // Preserve the persisted committed/uncommitted flag. Items saved as committed
      // (draft:false — they were bundle lines in the cart) MUST NOT come back as drafts:
      // if the cart has since been cleared, #syncFromCart's empty-cart branch drops
      // draft:false items (the cart is the source of truth) but KEEPS a genuine
      // build-from-scratch draft (draft:true). Forcing every restored item to draft:true
      // was what let a cleared bundle linger in the drawer across a reload (EA-3873).
      // Default to committed(false) for legacy drafts with no saved flag, so an
      // unbacked stale draft errs toward being dropped rather than resurrected.
      rebuilt.draft = item.draft === true;
      restored.push(rebuilt);
      seen.add(productId);
    }

    this.#selectedItems = restored;
    this.#normalizeAddOrders(this.#selectedItems);
    for (const productId of seen) {
      this.#applyCardState(productId);
    }
    this.#reorderCardsByBundle();
    this.#saveState();
  }

  /** Fetch the live cart, or null on error. */
  async #fetchCart() {
    try {
      // Shared with every other cart reader on the page (@theme/cart-state).
      return await fetchCartJson();
    } catch {
      return null;
    }
  }

  /** Re-sync the drawer with the cart on every cart change. */
  #handleCartUpdate = () => {
    // Nothing is built yet, and #hydrate reads the cart itself when it is.
    if (!this.#hydrated) return;
    this.#syncFromCart();
  };

  /**
   * Reflect the cart's committed bundle line items in the drawer, so the cards mirror
   * the cart and the bundle total (for the tier discount) covers the FULL bundle.
   * The cart is the single source of truth whenever it contains bundle products — the
   * persisted localStorage draft is used ONLY when the cart has no bundle products.
   */
  async #syncFromCart() {
    // Stale-response guard: this fires from #hydrate, open() and every cart:update,
    // so calls can overlap. Only the newest response may apply cart state — an older
    // one resolving late must not overwrite it.
    const gen = ++this.#cartSyncGen;
    const cart = await this.#fetchCart();
    if (gen !== this.#cartSyncGen) return; // superseded by a newer sync
    if (!cart || !Array.isArray(cart.items)) return;

    // Hide the footer's clear (X) icon once the cart holds any item — a committed
    // cart is tied to the chosen store, so the shopper can't clear the store here.
    this.#reflectStoreClearAvailability(cart.item_count || 0);

    // Did the cart hold committed bundle products the last time we synced? Captured
    // BEFORE #committedVariantIds is rebuilt below so the else-branch can tell a
    // cleared/removed bundle (flush the draft) apart from a never-committed
    // build-from-scratch draft (keep it). See the precedence note below.
    const hadCommittedBundles = this.#committedVariantIds.size > 0;

    // Bundle line items in the cart, keyed by product id.
    const cartBundles = new Map();
    for (const item of cart.items) {
      const props = item.properties || {};
      if (String(props[CART_PROPERTY_KEYS.itemType]) === CART_ITEM_TYPES.bundleLineItem) {
        cartBundles.set(Number(item.product_id), {
          variantId: Number(item.variant_id),
          quantity: Number(item.quantity) || 1,
        });
      }
    }

    // Rebuild each committed item from its drawer card (qty from cart). Products
    // not in the drawer's collections can't be shown → skipped.
    const cartItems = [];
    const committed = new Set();
    for (const [productId, info] of cartBundles) {
      committed.add(info.variantId);
      const rebuilt = this.#rebuildRestoredItem({
        productId,
        variantId: info.variantId,
        quantity: info.quantity,
      });
      if (rebuilt) {
        rebuilt.draft = false; // committed (came from the cart)
        cartItems.push(rebuilt);
      }
    }
    this.#committedVariantIds = committed;

    // Precedence: the CART is the single source of truth whenever it holds any bundle
    // products — the persisted localStorage draft is discarded so the drawer mirrors the
    // cart exactly (no phantom draft items lingering on top of committed lines).
    if (cartItems.length > 0) {
      this.#selectedItems = cartItems;
    } else if (hadCommittedBundles) {
      // The cart HELD committed bundle products and now has none: they were removed or
      // the cart was cleared. The committed bundle is abandoned, so the draft is flushed
      // too (localStorage removed by #saveState below) rather than leaving phantom draft
      // items behind. "Clear the cart → clear the drawer + page" — the cart is the source
      // of truth, never the other way round (EA-3873). Because the Bundle Builder drawer
      // is global (header-group), its #syncFromCart runs on the clear's cart:update from
      // ANY page, so the localStorage draft is flushed even when the clear happens
      // elsewhere — a later BB-page load then finds nothing to restore.
      this.#selectedItems = [];
    } else {
      // The cart never had bundle products — a genuine build-from-scratch draft. Keep the
      // in-progress additions (persisted across reload / navigation by #restoreState),
      // since there was no committed bundle to abandon.
      this.#selectedItems = this.#selectedItems.filter((i) => i.draft === true);
    }

    // Re-render every card's Add ↔ stepper state, then recompute progress +
    // footer so the tier discount uses the full bundle total.
    this.#normalizeAddOrders(this.#selectedItems);
    const pids = new Set(this.#selectedItems.map((i) => i.productId));
    this.querySelectorAll('.bb-card[data-product-id]').forEach((card) => {
      pids.add(Number(card.dataset.productId));
    });
    for (const pid of pids) this.#applyCardState(pid);
    this.#reorderCardsByBundle();
    this.#updateProgress();
    this.#updateFooter();
    this.#saveState();
  }

  /**
   * Render either the Add button or a quantity stepper on every card for a product.
   * @param {number} productId
   */
  #applyCardState(productId) {
    const item = this.#selectedItems.find((i) => i.productId === productId);
    const cards = this.querySelectorAll(`.bb-card[data-product-id="${productId}"]`);
    for (const card of cards) {
      const btn = card.querySelector('.bb-card__action');
      let stepper = card.querySelector('.bb-card__qty');

      if (item) {
        if (btn) btn.style.display = 'none';
        if (!stepper) {
          stepper = this.#buildStepper(productId);
          if (btn) btn.insertAdjacentElement('afterend', stepper);
          else card.querySelector('.bb-card__info')?.appendChild(stepper);
        }
        const value = stepper.querySelector('[data-bb-qty-value]');
        if (value) value.textContent = String(item.quantity);
        const minus = stepper.querySelector('[data-bb-qty-minus]');
        if (minus) minus.setAttribute('aria-label', item.quantity <= 1 ? 'Remove item' : 'Decrease quantity');
        this.#setProductSelected(card, item.variantTitle, item.price);
      } else {
        if (stepper) stepper.remove();
        if (btn) btn.style.display = '';
        this.#restoreProductPrice(card);
      }
    }
  }

  /**
   * Build a quantity stepper element bound to a product.
   * @param {number} productId
   * @returns {HTMLElement}
   */
  #buildStepper(productId) {
    const stepper = document.createElement('div');
    stepper.className = 'bb-card__qty';
    stepper.innerHTML = `
      <button type="button" class="bb-card__qty-btn" data-bb-qty-minus aria-label="Decrease quantity">
        <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M5 10H15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
      </button>
      <span class="bb-card__qty-value" data-bb-qty-value>1</span>
      <button type="button" class="bb-card__qty-btn" data-bb-qty-plus aria-label="Increase quantity">
        <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M10 5V15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path d="M5 10H15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
      </button>
    `;
    stepper.querySelector('[data-bb-qty-minus]').addEventListener('click', () => this.#changeQty(productId, -1));
    stepper.querySelector('[data-bb-qty-plus]').addEventListener('click', () => this.#changeQty(productId, 1));
    return stepper;
  }

  /**
   * Change a product's bundle quantity. Going below 1 removes it (restores Add).
   * @param {number} productId
   * @param {number} delta
   */
  #changeQty(productId, delta) {
    const item = this.#selectedItems.find((i) => i.productId === productId);
    if (!item) return;
    const next = item.quantity + delta;
    let removed = false;
    if (next < 1) {
      this.#removeByProduct(productId);
      removed = true;
    } else if (next > this.#maxQuantity) {
      return;
    } else {
      item.quantity = next;
    }
    this.#applyCardState(productId);
    // Mirror the move-to-front on add: when a product is removed (qty → 0), move its card
    // to the START of the unselected group. #reorderCardsByBundle keeps selected cards first
    // and lays the rest out in DOM order — the just-removed card is still physically at the
    // front of the DOM (it was selected), so it lands first among the unselected.
    if (removed) this.#reorderCardsByBundle();
    this.#updateProgress();
    this.#updateFooter();
    this.#saveState();
  }

  /**
   * Flash the first accordion's product cards ONCE — the same dim + sweep shimmer shown
   * during a store-change re-fetch, but a single pass — to draw the shopper's attention to
   * Step 1 when they tap the disabled "Add a product to continue" CTA (secondary-only
   * bundle). Restarts cleanly on repeated clicks (clear → reflow → re-apply) and clears
   * itself after one pass. Cards stay clickable throughout (no pointer-events lock).
   */
  #flashPrimaryAttention() {
    const step = this.querySelector('.bb-step[data-step="1"]');
    if (!step) return;
    // Bring the first accordion ("Choose your first item") into view — the shopper may have
    // scrolled down to Step 2, so scroll the drawer body to the top before flashing it.
    const main = this.querySelector('.bb-main');
    if (main && typeof main.scrollTo === 'function') {
      main.scrollTo({ top: 0, behavior: 'smooth' });
    }
    if (this.#attentionTimer) clearTimeout(this.#attentionTimer);
    step.removeAttribute('data-bb-attention');
    // Force a reflow so removing then re-adding the attribute restarts the CSS animation.
    void step.offsetWidth;
    step.setAttribute('data-bb-attention', '');
    this.#attentionTimer = setTimeout(() => {
      step.removeAttribute('data-bb-attention');
      this.#attentionTimer = null;
    }, 850);
  }

  /**
   * Show the chosen variant (name | price) on a card.
   * @param {HTMLElement} card
   * @param {string} variantTitle
   * @param {number} priceCents
   */
  #setProductSelected(card, variantTitle, priceCents) {
    const pricing = card.querySelector('.bb-card__pricing');
    if (!pricing) return;
    if (!this.#originalPricing.has(pricing)) {
      this.#originalPricing.set(pricing, pricing.innerHTML);
    }
    const label = variantTitle ? `${variantTitle} | ` : '';
    pricing.innerHTML = `<span class="bb-card__price">${this.#escapeHTML(label + this.#formatMoney(priceCents))}</span>`;
  }

  /**
   * Restore the original "Starting at" pricing on a card.
   * @param {HTMLElement} card
   */
  #restoreProductPrice(card) {
    const pricing = card.querySelector('.bb-card__pricing');
    if (pricing && this.#originalPricing.has(pricing)) {
      pricing.innerHTML = this.#originalPricing.get(pricing);
    }
  }

  /**
   * Escape text for safe HTML output
   * @param {string} str
   * @returns {string}
   */
  #escapeHTML(str) {
    const div = document.createElement('div');
    div.textContent = str == null ? '' : String(str);
    return div.innerHTML;
  }

  /**
   * Escape a value for an HTML attribute
   * @param {string} str
   * @returns {string}
   */
  #escapeAttr(str) {
    return (str == null ? '' : String(str))
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  /**
   * Format a price in cents using the shop's active money format (mirrors ea-cart-drawer).
   * @param {number} cents
   * @returns {string}
   */
  #formatMoney(cents) {
    return formatShopifyMoney(cents, this.dataset.moneyFormat || '${{amount}}', this.dataset.currency || 'USD');
  }

  /** Calculate bundle total in cents (price × quantity) */
  #getBundleTotal() {
    return this.#selectedItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  }

  /**
   * Map a bundle total to a fill percentage across the evenly-spaced tier markers.
   * The fill interpolates within each segment so it lands exactly on a marker
   * when its threshold is reached (markers are equally spaced, not value-scaled).
   * @param {number} baseTotal undiscounted selection subtotal (dollars)
   * @returns {number}
   */
  #progressPercent(baseTotal) {
    const markers = this.#markers;
    const n = markers.length;
    if (!n) return 0;
    let prevValue = 0;
    let prevPos = 0;
    for (let i = 0; i < n; i++) {
      const value = markers[i].threshold;
      const pos = ((i + 1) / n) * 100;
      if (baseTotal <= value) {
        if (value <= prevValue) return prevPos;
        const frac = Math.max(0, Math.min(1, (baseTotal - prevValue) / (value - prevValue)));
        return prevPos + frac * (pos - prevPos);
      }
      prevValue = value;
      prevPos = pos;
    }
    return 100;
  }

  /** Update the progress bar and threshold indicators */
  #updateProgress() {
    // Tier progress (fill, active dots, next tier) is measured on the undiscounted
    // selection subtotal.
    const totalCents = this.#getBundleTotal();
    const total = totalCents / 100;
    // What the customer actually pays after the bundle discount (same figure the
    // Add All to Cart CTA shows) — ONLY decides the free-delivery-unlocked prefix.
    const discountedCents = totalCents - this.#bundleSavingsCents();

    // Update progress bar fill (interpolated across the evenly-spaced markers)
    const fill = this.querySelector('.bundle-progress__fill');
    if (fill) {
      fill.style.width = `${this.#progressPercent(total)}%`;
    }

    // Update threshold dots (the solid start dot has no threshold — leave it alone)
    const dots = this.querySelectorAll('.bundle-progress__dot');
    for (const dot of dots) {
      if (dot.dataset.threshold == null) continue;
      const threshold = Number(dot.dataset.threshold);
      dot.classList.toggle('bundle-progress__dot--active', total >= threshold);
    }

    // Update progress text (anchored on the NEXT unreached tier; amount bold),
    // optionally prefixed with the merchant's "free delivery unlocked" message once
    // the discounted total reaches the free-delivery threshold. Once every tier is
    // reached, show a completion message.
    const textEl = this.querySelector('.bundle-progress__text');
    if (textEl) {
      const freeDelivery = Number(this.#discountConfig.freeDelivery) || 0;
      const fdUnlocked =
        freeDelivery > 0 && this.#freeDeliveryApplies() && discountedCents >= Math.round(freeDelivery * 100);
      const prefixText = (this.querySelector('.bundle-progress')?.dataset.bpFreeDeliveryUnlocked || '').trim();
      // Escaped merchant text + one space (Shopify trims trailing whitespace from text settings).
      const prefix = fdUnlocked && prefixText ? `${this.#escapeHTML(prefixText)} ` : '';

      const milestones = this.#markers && this.#markers.length ? this.#markers : [];
      const next = milestones.find((m) => total < m.threshold);
      if (next) {
        const remainingCents = Math.max(0, Math.round(next.threshold * 100) - totalCents);
        const reward = next.label ? next.label.toLowerCase() : 'the next milestone';
        const remainingFormatted = this.#formatMoney(remainingCents);
        // Escape the template, then drop the bold amount in (the only markup it carries).
        const template = this.#escapeHTML(this.#progressCopy('bpTextNext', 'Add {{ amount }} more to unlock {{ reward }}'));
        textEl.innerHTML =
          prefix +
          template
            .replace(/\{\{\s*amount\s*\}\}/g, `<strong>${this.#escapeHTML(remainingFormatted)}</strong>`)
            .replace(/\{\{\s*reward\s*\}\}/g, this.#escapeHTML(reward));
      } else if (milestones.length) {
        // All tiers reached — show the last (highest) tier's unlocked state.
        const last = milestones[milestones.length - 1];
        const reward = last.label ? last.label.toLowerCase() : 'all rewards';
        const template = this.#escapeHTML(this.#progressCopy('bpTextDone', "You've unlocked {{ reward }}!"));
        textEl.innerHTML = prefix + template.replace(/\{\{\s*reward\s*\}\}/g, this.#escapeHTML(reward));
      } else {
        textEl.textContent = '';
      }
    }
  }

  /**
   * Bundle discount savings in integer CENTS, mirroring the discount function: the
   * best (highest-threshold ≤ base-total) tier, with a percentage applied PER LINE
   * and rounded to the cent then summed (applying the percentage to the grand total
   * once rounds differently — that's why the drawer and cart could disagree by a few
   * cents); `upTo` caps the DISCOUNT AMOUNT (e.g. "25% off, up to $100"), not the
   * basis; never exceeds the base total. Shared by #updateFooter (savings row) and
   * #updateProgress (the discounted subtotal the free-delivery milestone measures
   * against) so the two never diverge.
   * @returns {number}
   */
  #bundleSavingsCents() {
    const totalCents = this.#getBundleTotal();
    const total = totalCents / 100;
    let bestTier = null;
    for (const tier of this.#discountConfig.tiers) {
      if (total >= tier.threshold && (!bestTier || tier.threshold > bestTier.threshold)) {
        bestTier = tier;
      }
    }
    if (!bestTier) return 0;
    let savingsCents = 0;
    if (bestTier.type === 'fixed') {
      savingsCents = Math.round(bestTier.value * 100);
    } else {
      const pct = bestTier.value / 100;
      for (const item of this.#selectedItems) {
        savingsCents += Math.round(item.price * item.quantity * pct);
      }
      if (bestTier.upTo != null) {
        savingsCents = Math.min(savingsCents, Math.round(bestTier.upTo * 100));
      }
    }
    return Math.min(savingsCents, totalCents);
  }

  /** Update the footer (total savings, ATC button text) */
  #updateFooter() {
    const totalCents = this.#getBundleTotal();
    const savingsCents = this.#bundleSavingsCents();
    const savings = savingsCents / 100;
    const finalPrice = (totalCents - savingsCents) / 100;

    // Update savings display
    const savingsAmount = this.querySelector('.bb-footer__savings-amount');
    if (savingsAmount) {
      savingsAmount.textContent = this.#formatMoney(Math.round(savings * 100));
    }

    const savingsRow = this.querySelector('.bb-footer__savings');
    if (savingsRow) {
      savingsRow.style.display = savings > 0 ? '' : 'none';
    }

    // Update ATC button
    const atcBtn = this.querySelector('.bb-footer__atc');
    if (atcBtn) {
      const baseText = this.dataset.addToCartText || 'Add All to Cart';
      // The bundle just needs at least #minBundleItems distinct variants, in any
      // mix/order of products and add-ons.
      const hasEnoughItems = this.#selectedItems.length >= this.#minBundleItems;
      // A bundle must contain at least one PRIMARY product (seo.hidden != 1) to be
      // orderable — secondary/hidden products can't stand alone in a bundle.
      const hasPrimary = this.#selectedItems.some((i) => i.seoHidden !== true);
      // A store MUST be selected to proceed to cart. When the bundle is otherwise
      // ready but no store is chosen, keep the CTA disabled and prompt selection.
      // The "add a primary product" prompt stays CLICKABLE (aria-disabled rather than a
      // native-disabled button) so tapping it can flash attention on the first accordion.
      // Every other blocked state is a genuine native-disabled button. Reset the
      // aria-disabled flag each pass so it never lingers into an enabled/other state.
      atcBtn.removeAttribute('aria-disabled');
      if (this.#priorityLoading) {
        // Priority-1 availability (arrangements + v4 for the bundle/cart) is still in
        // flight after a store change — the bundle may still lose an item, so block
        // checkout until it settles (EA-3873).
        atcBtn.textContent = baseText;
        atcBtn.disabled = true;
      } else if (!hasEnoughItems) {
        atcBtn.textContent = baseText;
        atcBtn.disabled = true;
      } else if (!hasPrimary) {
        atcBtn.textContent = this.dataset.addPrimaryText || 'Add a product to continue';
        // Looks disabled (grey, via the [aria-disabled] CSS) but stays clickable so the
        // click can nudge the shopper to the first accordion — see handleAddToCart.
        atcBtn.disabled = false;
        atcBtn.setAttribute('aria-disabled', 'true');
      } else if (!this.#hasStoreSelected()) {
        atcBtn.textContent = this.dataset.selectStoreText || 'Select a store to continue';
        atcBtn.disabled = true;
      } else {
        atcBtn.textContent = `${baseText} - ${this.#formatMoney(Math.round(finalPrice * 100))}`;
        atcBtn.disabled = false;
      }
    }
  }

  /** Handle ATC button click */
  async handleAddToCart(event) {
    event.preventDefault();

    // Mirror the footer button's enabled state: at least #minBundleItems distinct variants.
    if (this.#selectedItems.length < this.#minBundleItems) return;

    // A bundle must contain at least one PRIMARY product (seo.hidden != 1) to be
    // orderable. Guard here too (defense in depth against a race that briefly
    // enables the CTA) — secondary-only bundles can't proceed to cart.
    if (!this.#selectedItems.some((i) => i.seoHidden !== true)) {
      this.#updateFooter();
      // The CTA is aria-disabled here (secondary-only bundle). Instead of doing nothing,
      // flash the first accordion once to point the shopper at where a primary product is.
      this.#flashPrimaryAttention();
      return;
    }

    /* A store MUST be selected to proceed to cart. Defence in depth against a race
       that briefly enables the CTA — #updateFooter already disables it and labels it
       "Select a store to continue", which is the whole message.

       It used to open the header pop-up from here. Nothing in the Bundle Builder does
       that any more: the selection is asked for inside the variant drawer, on the
       product, and a pop-up thrown over a drawer the shopper is already standing in was
       the behaviour this flow was rebuilt to remove. Refusing and re-labelling the CTA
       says the same thing without moving them. */
    if (!this.#hasStoreSelected()) {
      this.#updateFooter();
      return;
    }

    const atcBtn = this.querySelector('.bb-footer__atc');
    if (atcBtn) {
      atcBtn.disabled = true;
      // Show a spinner (not "Adding..." text) for consistency with the other CTAs.
      atcBtn.classList.add('bb-footer__atc--loading');
    }

    try {
      // A shipment-only zip carries no storeId; name the shipping store first or the
      // add-to-cart interceptor refuses the add (EA-4071).
      await this.#adoptShipmentStoreForBundle();

      // Reconcile the cart's bundle lines to EXACTLY match the drawer draft:
      // update changed quantities, remove lines the shopper dropped, and add new
      // ones. Identical variants merge into one cart line (discount function keys
      // off `_item_type: bundle_line_item` alone).
      const cartNow = await this.#fetchCart();
      const cartItems = cartNow && Array.isArray(cartNow.items) ? cartNow.items : [];

      // A store MUST stay selected through checkout. The shopper can clear it
      // (footer clear button) during the async #fetchCart above, so re-check
      // before proceeding: if it's gone, abort cleanly rather than proceeding to cart
      // with a store-less selection. Declared here (before the no-change branch) so
      // every downstream path — the no-change open included — is guarded; re-checked
      // before each mutation below since more `await`s intervene.
      //
      // Aborting is the whole response: #updateFooter puts the CTA back to "Select a
      // store to continue", and the shopper is already in the drawer that asks for it.
      const abortIfNoStore = () => {
        if (this.#hasStoreSelected()) return false;
        if (atcBtn) atcBtn.classList.remove('bb-footer__atc--loading');
        this.#updateFooter();
        return true;
      };
      if (abortIfNoStore()) return;

      // Existing bundle lines: variantId → { key, quantity }.
      const existing = new Map();
      for (const it of cartItems) {
        if (String((it.properties || {})[CART_PROPERTY_KEYS.itemType]) === CART_ITEM_TYPES.bundleLineItem) {
          existing.set(Number(it.variant_id), { key: it.key, quantity: Number(it.quantity) || 0 });
        }
      }

      // Desired bundle from the drawer draft: variantId → item.
      const desired = new Map();
      for (const item of this.#selectedItems) desired.set(item.variantId, item);

      // Qty changes + removals (0) for existing lines.
      const updates = {};
      // What this submit ADDS — quantity increases here plus the new lines below —
      // for analytics: the CartAddEvent resource is the whole cart, so cart_add reads
      // this to report only the added lines and quantities (assets/ea-datalayer.js
      // bundleAddedLines).
      // Each entry carries the cart line `key` so analytics prices the exact line that
      // grew, even when two lines share a variant; new lines get theirs from the
      // re-read cart below (the one bundle line whose key was not in the cart before).
      /** @type {Array<{variantId: number, quantity: number, key?: string}>} */
      const bundleAdded = [];
      for (const [variantId, line] of existing) {
        const wantedQty = desired.has(variantId) ? desired.get(variantId).quantity : 0;
        if (wantedQty !== line.quantity) updates[line.key] = wantedQty;
        if (wantedQty > line.quantity) bundleAdded.push({ variantId, quantity: wantedQty - line.quantity, key: line.key });
      }

      // New bundle lines to add (in the draft, not yet in the cart).
      const items = [];
      let lineOrder = 0;
      for (const item of this.#selectedItems) {
        if (existing.has(item.variantId)) continue;
        lineOrder += 1;
        const bundleProps = {
          [CART_PROPERTY_KEYS.itemType]: CART_ITEM_TYPES.bundleLineItem,
          [CART_PROPERTY_KEYS.isOnGoody]: 'false',
          [CART_PROPERTY_KEYS.lineItemOrder]: String(lineOrder),
        };
        // Carry the marketplace variant id (country-prefixed, e.g. "US-18517") so the
        // pre-checkout cart-availability check can validate bundle lines the same way it
        // validates every other orderable EA product — see #buildAvailabilityProductIds
        // in ea-cart-drawer.js. Only stamped when present, mirroring the parent-line
        // convention in personalization-drawer.js.
        // Canonical "CC-digits": the card attribute this came from is rendered from a
        // metafield Translate & Adapt rewrites on non-English pages ("É.-U.-18517",
        // EA-4392), and the backend reads this property as-is.
        const externalVariantId = canonicalEaId(item.externalVariantId);
        if (externalVariantId) {
          bundleProps[CART_PROPERTY_KEYS.externalVariantId] = externalVariantId;
        }
        // Product reference, matching what the personalization drawer stamps on parent
        // lines — a bundle line is a real product with its own PDP, so the line has to
        // resolve back to it. The bare handle, not a URL: a URL would bake in whichever
        // market the shopper was on when they added. Sourced from `data-handle` on the
        // card button (snippets/bb-product-card.liquid) via BundleItem.handle. Guarded
        // like the id above — a blank property is dropped by /cart/add.js anyway.
        if (item.handle) {
          bundleProps[CART_PROPERTY_KEYS.productHandle] = item.handle;
        }
        if (item.isAddon) {
          bundleProps[CART_PROPERTY_KEYS.isBundleAddon] = 'true';
        }
        // Product-level arrangement id ("US-4567") from the card's data-arrangement-id
        // (snippets/bb-product-card.liquid) — the same property the personalization
        // drawer stamps on parent lines, read by analytics as product.arrangementId.
        // Guarded like the ids above: omitted when the product has none.
        const arrangementId = canonicalEaId(this.#arrangementIdForProduct(item.productId));
        if (arrangementId) {
          bundleProps[CART_PROPERTY_KEYS.arrangementId] = arrangementId;
        }
        items.push({ id: item.variantId, quantity: item.quantity, properties: bundleProps });
        bundleAdded.push({ variantId: item.variantId, quantity: item.quantity });
      }

      const hasUpdates = Object.keys(updates).length > 0;

      // Nothing changed — the cart already matches the drawer. Just open it.
      if (!hasUpdates && items.length === 0) {
        // Still reconcile the BYOB flag: bundle lines already present but byob unset (or a
        // non-bundle cart still carrying a stale byob) must be corrected even when there is
        // nothing to add/update. Best-effort so a flag write never blocks the open.
        await this.#syncByobAttribute(
          cartNow ? cartNow.attributes : undefined,
          this.#selectedItems.length > 0,
        ).catch(() => {});
        if (atcBtn) atcBtn.classList.remove('bb-footer__atc--loading');
        this.#updateFooter();
        this.close();
        if (cartNow) {
          document.dispatchEvent(
            new CartAddEvent(cartNow, this.id || 'ea-bundle-builder-drawer', {
              source: 'ea-bundle-builder-drawer',
              itemCount: cartNow.item_count || 0,
              bundleAdded: [],
            }),
          );
        }
        return;
      }

      // Abort if a request hangs so the button never sticks on the spinner.
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);
      try {
        // 1) Apply qty changes + removals to existing bundle lines.
        if (hasUpdates) {
          if (abortIfNoStore()) return;
          const upRes = await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates }),
            signal: controller.signal,
          });
          if (!upRes.ok) throw new Error(`Cart update failed: ${upRes.status}`);
        }

        // 2) Add the new bundle lines.
        if (items.length) {
          if (abortIfNoStore()) return;
          const addRes = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items }),
            signal: controller.signal,
          });
          // Cart-conflict "keep my cart" → 409: nothing added, restore footer.
          if (addRes.status === 409) {
            if (atcBtn) atcBtn.classList.remove('bb-footer__atc--loading');
            this.#updateFooter();
            return;
          }
          if (!addRes.ok) throw new Error(`Cart add failed: ${addRes.status}`);
        }

        // 3) Flag the cart as a Build-Your-Own-Bundle order for downstream fulfillment
        // (read from cart.attributes). The cart's bundle lines now mirror the draft, so
        // set byob true when the draft holds items and clear it when a removal emptied
        // the bundle. Compared against the pre-mutation attributes (mutations above don't
        // touch attributes), so an unchanged flag issues no request.
        if (abortIfNoStore()) return;
        await this.#syncByobAttribute(
          cartNow ? cartNow.attributes : undefined,
          this.#selectedItems.length > 0,
          controller.signal,
        );
      } finally {
        clearTimeout(timeoutId);
      }

      // Re-read the reconciled cart and open the cart drawer with it. The draft
      // is left intact (it mirrors the cart) and re-syncs on the next open.
      const finalCart = await this.#fetchCart();
      // Resolve the key of each newly added bundle line: same variant, bundle line,
      // and a key that was not in the cart before this submit.
      if (finalCart && Array.isArray(finalCart.items)) {
        const keysBefore = new Set(cartItems.map((it) => it.key));
        const claimed = new Set();
        for (const add of bundleAdded) {
          if (add.key) continue;
          const line = finalCart.items.find(
            (it) =>
              Number(it.variant_id) === Number(add.variantId) &&
              !keysBefore.has(it.key) &&
              !claimed.has(it.key) &&
              String((it.properties || {})[CART_PROPERTY_KEYS.itemType]) === CART_ITEM_TYPES.bundleLineItem,
          );
          if (line) {
            add.key = line.key;
            claimed.add(line.key);
          }
        }
      }
      if (atcBtn) atcBtn.classList.remove('bb-footer__atc--loading');
      this.#updateFooter();
      this.close();
      document.dispatchEvent(
        new CartAddEvent(finalCart || cartNow, this.id || 'ea-bundle-builder-drawer', {
          source: 'ea-bundle-builder-drawer',
          itemCount: finalCart && finalCart.item_count ? finalCart.item_count : items.length,
          bundleAdded,
        }),
      );
    } catch (error) {
      console.error('[BundleBuilder] ATC error:', error);
      if (atcBtn) {
        atcBtn.classList.remove('bb-footer__atc--loading');
        atcBtn.textContent = this.dataset.atcErrorText || 'Error - Try Again';
        atcBtn.disabled = false;
      }
    }
  }
}

customElements.define('ea-bundle-builder-drawer', EaBundleBuilderDrawer);
